import type { LinkTelemetryGroup } from './linkDetailTelemetry';
import 'maplibre-gl/dist/maplibre-gl.css';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArcLayer, PathLayer, ScatterplotLayer } from '@deck.gl/layers';
import type { Layer, PickingInfo } from '@deck.gl/core';
import maplibregl, { Map as MapLibreMap } from 'maplibre-gl';
import { PMTiles, Protocol } from 'pmtiles';

import { AnimatedArcLayer } from './AnimatedArcLayer';
import {
  buildHubDeviceHealthLayers,
  hubDeviceStatusRadiusForZoom,
  readDeviceHealthDeckPalette
} from './deviceHealthLayers';
import {
  projectHubDeviceMarkers,
  type HubDeviceMarkerDatum
} from './deviceHealthProjection';
import { MapLibreDeckOverlay } from './MapLibreDeckOverlay';
import type { MapLink, StatusAlert, StatusMapSnapshot, StatusTelemetryClient } from './statusTelemetryApi';
import { toLegacyMapLinkInput } from './statusMapProjection';
import { isUnconfirmedTopologyLink } from './topologyLinkPresentation';
import { useStatusTelemetryMapController, type MapTransportState } from './useStatusTelemetryMapController';
import { utilizationPctToRatio } from './utilization';
import {
  buildLinkDetailTelemetryViewModel
} from './linkDetailTelemetry';
import { LinkDetailDrawer } from './presentation/LinkDetailDrawer';
import { HubDeviceDrawer } from './presentation/HubDeviceDrawer';
import { useHubDeviceDrawerController } from './useHubDeviceDrawerController';

const DEFAULT_CENTER: [number, number] = [-96.9, 39.8];
const DEFAULT_ZOOM = 4.2;
const DEFAULT_PITCH = 22;
const NETWORK_STYLE_URL = '/api/v1/status/map-style';
const BASEMAP_PMTILES_URL = '/api/v1/status/basemap/pmtiles';
const TRAFFIC_URL = '/api/v1/status/network-links/traffic';
const TRAFFIC_SERIES_URL = '/api/v1/status/network-links/traffic/series';
const PMTILES_MAGIC = 'PMTiles';
const LINK_STATUS_WARNING = 'warning' as const;
const LINK_STATUS_MISSING = 'missing telemetry data' as const;
const FALLBACK_STYLE = {
  version: 8,
  name: 'Status Map (Fallback)',
  sources: {},
  layers: [
    {
      id: 'background',
      type: 'background',
      paint: {
        'background-color': 'rgb(8, 12, 18)'
      }
    }
  ]
};


const PACKET_COLOR_HEALTHY: [number, number, number, number] = [220, 255, 220, 255];
const PACKET_COLOR_WARNING: [number, number, number, number] = [255, 205, 120, 240];
const PACKET_COLOR_BROKEN: [number, number, number, number] = [255, 120, 90, 240];
const PACKET_COLOR_MISSING: [number, number, number, number] = [186, 130, 255, 230];
const ARC_HIGHLIGHT_COLOR: [number, number, number, number] = [230, 255, 255, 220];
const LINK_COLOR_HEALTHY_LOW: [number, number, number] = [0, 255, 65];
const LINK_COLOR_HEALTHY_WARN: [number, number, number] = [255, 200, 80];
const LINK_COLOR_HEALTHY_HIGH: [number, number, number] = [255, 90, 60];
const LINK_COLOR_WARNING: [number, number, number] = [242, 191, 92];
const LINK_COLOR_BROKEN: [number, number, number] = [140, 150, 170];
const LINK_COLOR_MISSING: [number, number, number] = [176, 115, 255];
const LINK_CAPACITY_COLOR: [number, number, number] = [80, 170, 210];
const LINK_UTIL_WARN = 0.5;
const LINK_UTIL_CRIT = 0.8;
const LINK_FADE_START_SEC = 5 * 60;
const LINK_FADE_END_SEC = 30 * 60;
const LINK_FADE_MIN_ALPHA = 0.18;
const LINK_LINE_ALPHA = 180;
const LINK_LINE_ALPHA_BROKEN = 220;
const LINK_CAPACITY_ALPHA = 120;
const LINK_RING_ALPHA = 160;
const LINK_PIPE_ALPHA = 80;
const LINK_MISSING_PULSE_MS = 3600;
const PACKET_REPRESENTS_PPS = 25000;
const PACKET_RATE_BROKEN_PER_SEC = 0.2;
const PACKET_TRAVEL_SEC = 2.4;
const PACKET_TAIL_SEC = 0.2;
const PACKET_MAX_ACTIVE = 6000;
const PACKET_RATE_SCALE_DEFAULT = 1;
const PACKET_RATE_SCALE_MIN = 0.25;
const PACKET_RATE_SCALE_MAX = 3;
const PACKET_RATE_SCALE_STEP = 0.05;
const MIN_PPS_PER_LINK = PACKET_REPRESENTS_PPS / 10;
const MAX_DOTS_PER_LINK_PER_SEC = 20;
const MAX_PPS_PER_LINK = PACKET_REPRESENTS_PPS * MAX_DOTS_PER_LINK_PER_SEC;

export const retainActivePackets = <T extends { endTime: number }>(
  packets: T[],
  nowSec: number,
  tailSec = PACKET_TAIL_SEC
): T[] => packets.filter(packet => packet.endTime + tailSec >= nowSec);

export const packetEmissionFrame = (
  nowSec: number,
  lastEmitSec: number
): { delta: number; lastEmitSec: number } => {
  if (!nowSec) return { delta: 0, lastEmitSec };
  if (!lastEmitSec) return { delta: 0, lastEmitSec: nowSec };
  return {
    delta: Math.max(0, Math.min(nowSec - lastEmitSec, 0.25)),
    lastEmitSec: nowSec
  };
};

export const admitPacketCandidates = <A extends { endTime: number }, L extends { endTime: number }>(
  active: { packets: A[]; loopPackets: L[]; preferLoopNext?: boolean },
  candidates: { packets: A[]; loopPackets: L[] },
  maxActive: number,
  nowSec: number,
  tailSec = PACKET_TAIL_SEC
): { packets: A[]; loopPackets: L[]; preferLoopNext?: boolean } => {
  const packets = retainActivePackets(active.packets, nowSec, tailSec);
  const loopPackets = retainActivePackets(active.loopPackets, nowSec, tailSec);
  let capacity = Math.max(0, maxActive - packets.length - loopPackets.length);
  if (capacity <= 0 || (!candidates.packets.length && !candidates.loopPackets.length)) {
    if (packets.length === active.packets.length && loopPackets.length === active.loopPackets.length) return active;
    return { packets, loopPackets, preferLoopNext: active.preferLoopNext };
  }

  let packetSlots = 0;
  let loopSlots = 0;
  let preferLoopNext = active.preferLoopNext ?? false;
  const totalCandidates = candidates.packets.length + candidates.loopPackets.length;
  if (candidates.packets.length && candidates.loopPackets.length && capacity === 1) {
    if (preferLoopNext) loopSlots = 1;
    else packetSlots = 1;
    preferLoopNext = !preferLoopNext;
  } else if (totalCandidates > 0) {
    packetSlots = Math.min(
      candidates.packets.length,
      Math.round(capacity * (candidates.packets.length / totalCandidates))
    );
    loopSlots = Math.min(candidates.loopPackets.length, capacity - packetSlots);
    if (capacity >= 2 && candidates.packets.length && candidates.loopPackets.length) {
      if (packetSlots === 0) {
        packetSlots = 1;
        loopSlots = Math.max(0, loopSlots - 1);
      }
      if (loopSlots === 0) {
        loopSlots = 1;
        packetSlots = Math.max(0, packetSlots - 1);
      }
    }
  }

  capacity -= packetSlots + loopSlots;
  const remainingPackets = candidates.packets.length - packetSlots;
  const extraPackets = Math.min(capacity, remainingPackets);
  packetSlots += extraPackets;
  capacity -= extraPackets;
  loopSlots += Math.min(capacity, candidates.loopPackets.length - loopSlots);

  return {
    packets: packets.concat(candidates.packets.slice(0, packetSlots)),
    loopPackets: loopPackets.concat(candidates.loopPackets.slice(0, loopSlots)),
    preferLoopNext
  };
};

const HIGHLIGHT_WIDTH = 4;
const ARC_WIDTH = 2;
const ARC_WIDTH_MIN = 1;
const ARC_WIDTH_MAX = 4;
const ARC_PIPE_WIDTH_MIN = 2.5;
const ARC_PIPE_WIDTH_MAX = 7;
const ARC_WIDTH_SPEED_MIN_MBPS = 10;
const ARC_WIDTH_SPEED_MAX_MBPS = 10000;
const ARC_WIDTH_SPEED_EXPONENT = 1.2;
const ARC_HEIGHT = 0.3;
const ARC_HEIGHT_MIN = 0.12;
const ARC_HEIGHT_MAX = 0.7;
const ARC_LANE_HEIGHT_STEP = 0.06;
const ARC_SEGMENTS = 90;
const ARC_GREAT_CIRCLE = false;
const CO_DISTANCE_METERS = 30;
const CO_LOOP_RADIUS_METERS = 18;
const CO_LOOP_SEGMENTS = 28;
const CO_RING_WIDTH = 1.6;
const CO_RING_WIDTH_METERS = 0.6;
const CO_DOT_RADIUS_METERS = 1.2;
const CO_LOOP_LANE_OFFSET_METERS = 2;
const CO_LOOP_HIT_WIDTH_METERS = CO_LOOP_LANE_OFFSET_METERS * 1.2;
const CO_DOT_RADIUS = 4;
const CO_DOT_RADIUS_MIN = 2;
const CO_DOT_RADIUS_MAX = 6;
const DEBUG_ONLY =
  typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('debugOnly');
const DEBUG_CO_RINGS =
  typeof window !== 'undefined' &&
  (new URLSearchParams(window.location.search).has('debugCo') || DEBUG_ONLY);
const DEBUG_ENDPOINTS =
  typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('debugEndpoints');
const DEMO_NETWORK =
  typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('demoNetwork');
const DEBUG_AUTOPAN =
  typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('debugAutoPan');
const DEBUG_CO_CENTER: [number, number] = [-87.6298, 41.8781];
const DEBUG_CO_RADII_METERS = [18, 28, 38];
const DEBUG_CENTER_COLOR: [number, number, number, number] = [255, 120, 90, 255];
const EARTH_RADIUS_METERS = 6371008.8;
const TRAFFIC_QUERY_MIN_INTERVAL_MS = 10000;
const TRAFFIC_POLL_INTERVAL_MS = 60000;
const TRAFFIC_STALE_WARN_MS = TRAFFIC_POLL_INTERVAL_MS * 2;
const TRAFFIC_STALE_CRIT_MS = TRAFFIC_POLL_INTERVAL_MS * 5;
const TRAFFIC_SERIES_RANGE_SECONDS = 60 * 60;
const TRAFFIC_SERIES_RANGE_OPTIONS = [
  { label: 'Real time', seconds: 5 * 60, realtime: true },
  { label: '5m', seconds: 5 * 60 },
  { label: '15m', seconds: 15 * 60 },
  { label: '30m', seconds: 30 * 60 },
  { label: '1h', seconds: 60 * 60 },
  { label: '3h', seconds: 3 * 60 * 60 },
  { label: '6h', seconds: 6 * 60 * 60 },
  { label: '12h', seconds: 12 * 60 * 60 },
  { label: '1d', seconds: 24 * 60 * 60 },
  { label: '7d', seconds: 7 * 24 * 60 * 60 },
  { label: '30d', seconds: 30 * 24 * 60 * 60 },
  { label: '90d', seconds: 90 * 24 * 60 * 60 },
  { label: '1y', seconds: 365 * 24 * 60 * 60 }
] as const;
const TRAFFIC_SERIES_REALTIME_POLL_INTERVAL_MS = 5000;
const TRAFFIC_SERIES_DEFAULT_POLL_INTERVAL_MS = 60000;
const TRAFFIC_SERIES_REALTIME_TICK_MS = 1000;
const TRAFFIC_SERIES_DEFAULT_TICK_MS = 60000;
const AUTO_CAMERA_MIN_INTERVAL_MS = 60000;
const AUTO_CAMERA_ENABLED = false;
const AUTO_PAN_PITCH = 85;
const AUTO_PAN_DURATION_MS = 2000;
const AUTO_PAN_ROTATE_DEGREES = 25;
const AUTO_PAN_ROTATE_STEP_DEG = 6;
const AUTO_PAN_ROTATE_STEP_MS = 2400;
const BROKEN_CAROUSEL_IDLE_MS = 12000;
const BROKEN_CAROUSEL_INTERVAL_MS = 12000;
const BROKEN_CAROUSEL_STABLE_MS = 0;

let pmtilesProtocol: Protocol | null = null;
let pmtilesProtocolRegistered = false;
const pmtilesRegistry = new Map<string, PMTiles>();

const ensurePMTilesProtocol = (): Protocol => {
  if (!pmtilesProtocol) {
    pmtilesProtocol = new Protocol();
  }
  if (!pmtilesProtocolRegistered) {
    maplibregl.addProtocol('pmtiles', pmtilesProtocol.tile);
    pmtilesProtocolRegistered = true;
  }
  return pmtilesProtocol;
};

const toAbsoluteUrl = (url: string): string => {
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  if (url.startsWith('/')) return `${window.location.origin}${url}`;
  return new URL(url, window.location.href).toString();
};

const cloneStyle = <T,>(value: T): T => {
  return JSON.parse(JSON.stringify(value)) as T;
};

const findBasemapSourceKey = (sources: Record<string, any>): string | null => {
  if (sources.openmaptiles) return 'openmaptiles';
  const entry = Object.entries(sources).find(([, source]) => source?.type === 'vector');
  return entry ? entry[0] : null;
};

const registerPmtiles = (url: string): string => {
  const protocol = ensurePMTilesProtocol();
  const absolute = toAbsoluteUrl(url);
  if (!pmtilesRegistry.has(absolute)) {
    const instance = new PMTiles(absolute);
    pmtilesRegistry.set(absolute, instance);
    protocol.add(instance);
  }
  return absolute;
};

const buildBaseStyle = (baseStyle: Record<string, any> | null, basemapAvailable: boolean, basemapURL = BASEMAP_PMTILES_URL): Record<string, any> => {
  if (!baseStyle || !basemapAvailable) {
    return FALLBACK_STYLE;
  }
  const styleBase = cloneStyle(baseStyle);
  if (typeof styleBase.sprite === 'string') {
    styleBase.sprite = toAbsoluteUrl(styleBase.sprite);
  }
  if (typeof styleBase.glyphs === 'string') {
    styleBase.glyphs = toAbsoluteUrl(styleBase.glyphs);
  }
  const sources =
    styleBase.sources && typeof styleBase.sources === 'object' ? { ...styleBase.sources } : ({} as Record<string, any>);
  const basemapUrl = registerPmtiles(basemapURL);
  const basemapKey = findBasemapSourceKey(sources);
  const basemapSource = {
    ...(basemapKey ? sources[basemapKey] ?? {} : {}),
    type: 'vector',
    url: `pmtiles://${basemapUrl}`
  };
  if (basemapKey) {
    sources[basemapKey] = basemapSource;
  } else {
    sources.openmaptiles = basemapSource;
  }
  styleBase.sources = sources;
  if (Array.isArray(styleBase.layers)) {
    const labelKeywords = [
      'place',
      'settlement',
      'city',
      'town',
      'village',
      'hamlet',
      'road',
      'street',
      'transportation',
      'highway',
      'motorway',
      'trunk',
      'primary',
      'secondary',
      'tertiary',
      'residential'
    ];
    styleBase.layers = styleBase.layers.map((layer: Record<string, any>) => {
      if (!layer || layer.type !== 'symbol') return layer;
      const layout = layer.layout ?? {};
      if (!layout['text-field']) return layer;
      const idText = `${layer.id ?? ''} ${layer['source-layer'] ?? ''}`.toLowerCase();
      const filterText = layer.filter ? JSON.stringify(layer.filter).toLowerCase() : '';
      const isPlaceLabel = labelKeywords.some(key => idText.includes(key) || filterText.includes(key));
      if (!isPlaceLabel) return layer;
      const nextLayer = { ...layer };
      nextLayer.layout = { ...layout };
      nextLayer.paint = { ...(layer.paint ?? {}) };
      const size = nextLayer.layout['text-size'];
      if (typeof size === 'number') {
        nextLayer.layout['text-size'] = Math.max(size + 1, size * 1.2);
      }
      nextLayer.paint['text-color'] = 'rgba(240, 248, 255, 0.95)';
      nextLayer.paint['text-halo-color'] = 'rgba(0, 0, 0, 0.7)';
      nextLayer.paint['text-halo-width'] = Math.max(1.2, Number(nextLayer.paint['text-halo-width'] ?? 0) || 0);
      nextLayer.paint['text-halo-blur'] = Math.max(0.6, Number(nextLayer.paint['text-halo-blur'] ?? 0) || 0);
      return nextLayer;
    });
  }
  return styleBase;
};

const checkPmtilesHeader = async (url: string): Promise<boolean> => {
  try {
    const res = await fetch(toAbsoluteUrl(url), {
      cache: 'no-store',
      headers: {
        Range: 'bytes=0-7'
      }
    });
    if (!res.ok) return false;
    const buffer = new Uint8Array(await res.arrayBuffer());
    const magic = new TextDecoder().decode(buffer.slice(0, PMTILES_MAGIC.length));
    return magic === PMTILES_MAGIC;
  } catch {
    return false;
  }
};

type LinkStatus = 'healthy' | typeof LINK_STATUS_WARNING | 'broken' | typeof LINK_STATUS_MISSING;
type PacketDirection = 'forward' | 'backward';

type MissingTelemetry = {
  source?: string[];
  target?: string[];
};

type LinkStatusReason = {
  code?: string;
  summary?: string;
  details?: string[];
  source_oper_status?: number;
  target_oper_status?: number;
  source_out_bps?: number;
  source_in_bps?: number;
  target_out_bps?: number;
  target_in_bps?: number;
  asymmetry_ratio?: number;
  min_load_threshold_bps?: number;
  near_zero_threshold_bps?: number;
  ratio_threshold?: number;
};

type LinkMember = {
  source_device_id?: string;
  source_interface_id?: string;
  source_device_name?: string;
  source_interface_name?: string;
  target_device_id?: string;
  target_interface_id?: string;
  target_device_name?: string;
  target_interface_name?: string;
  evidence_types?: string[];
  capacity_bps?: number;
  ingress_bps?: number;
  egress_bps?: number;
  ingress_utilization_pct?: number;
  egress_utilization_pct?: number;
};

export type Link = {
  id: string;
  source: [number, number];
  target: [number, number];
  renderSource?: [number, number];
  renderTarget?: [number, number];
  renderHeight?: number;
  status: LinkStatus;
  status_reason?: LinkStatusReason;
  missing_telemetry?: MissingTelemetry;
  bandwidth_mbps: number;
  updated_at: string;
  updated_at_ms?: number;
  source_co_id?: number;
  target_co_id?: number;
  source_co_lat?: number;
  source_co_lng?: number;
  target_co_lat?: number;
  target_co_lng?: number;
  source_device_id?: string;
  target_device_id?: string;
  source_interface_id?: string;
  target_interface_id?: string;
  link_type?: string;
  topology_status?: string;
  topology_reason?: string;
  topology_confidence?: number;
  topology_evidence_types?: string[];
  member_links?: LinkMember[];
  ingress_utilization_pct?: number;
  egress_utilization_pct?: number;
  telemetry_host_a?: string;
  telemetry_host_b?: string;
  telemetry_ifindex_a?: number;
  telemetry_ifindex_b?: number;
  telemetry_ifname_a?: string;
  telemetry_ifname_b?: string;
};

type Hub = {
  id: string;
  external_id?: string;
  name?: string;
  location?: [number, number];
  location_source?: string;
};

const hubLookupKeys = (hub: Hub) => {
  const keys = new Set<string>();
  const id = parseOptionalString(hub.id);
  if (id) keys.add(id);
  const idNumeric = parseHubNumericKey(hub.id);
  if (idNumeric) keys.add(idNumeric);
  const externalNumeric = parseHubNumericKey(hub.external_id);
  if (externalNumeric) keys.add(externalNumeric);
  return Array.from(keys);
};

type LinkTraffic = {
  id: string;
  source_bps: number;
  source_in_bps?: number;
  target_bps: number;
  target_in_bps?: number;
  source_pps: number;
  target_pps: number;
  source_status?: number;
  target_status?: number;
  source_up?: boolean;
  target_up?: boolean;
  source_light_dbm?: number;
  target_light_dbm?: number;
};

type TrafficResponse = {
  server_time?: string;
  telemetry_updated_at?: string;
  enabled?: boolean;
  links?: LinkTraffic[];
};

type TrafficLinkRequest = {
  id: string;
  source_host?: string;
  source_ifindex?: number;
  target_host?: string;
  target_ifindex?: number;
};

type TrafficSeriesPoint = {
  ts: number;
  value: number;
};

type TrafficSeriesMetric = {
  name: string;
  label: string;
  unit?: string;
  points?: TrafficSeriesPoint[];
};

type TrafficSeriesSide = {
  host?: string;
  oid?: number;
  metrics?: TrafficSeriesMetric[];
  groups?: LinkTelemetryGroup[];
};

type TrafficSeriesResponse = {
  server_time?: string;
  enabled?: boolean;
  link_id?: string;
  range_seconds?: number;
  source?: TrafficSeriesSide;
  target?: TrafficSeriesSide;
  error?: string;
  diagnostic?: string;
};

type SeriesBucketKey = 'bandwidth' | 'errors' | 'packets' | 'temperature' | 'optics';

type TrafficSeriesRequestLink = Pick<
  Link,
  | 'id'
  | 'source_device_id'
  | 'source_interface_id'
  | 'telemetry_ifindex_a'
  | 'target_device_id'
  | 'target_interface_id'
  | 'telemetry_ifindex_b'
>;

export const buildTrafficSeriesRequest = (
  linkID: string,
  rangeSeconds: number,
  realtime: boolean,
  group?: 'interface' | 'sensors'
) => ({
  link: { id: linkID },
  rangeSeconds,
  realtime,
  ...(group ? { group } : {})
});

export const mergeTrafficSeriesGroups = (traffic?: TrafficSeriesResponse, sensors?: TrafficSeriesResponse): TrafficSeriesResponse => {
  const mergeSide = (a?: TrafficSeriesSide, b?: TrafficSeriesSide): TrafficSeriesSide | undefined =>
    a || b ? { ...a, ...b, metrics: [...(a?.metrics ?? []), ...(b?.metrics ?? [])], groups: [...(a?.groups ?? []), ...(b?.groups ?? [])] } : undefined;
  return {
    ...sensors,
    ...traffic,
    source: mergeSide(traffic?.source, sensors?.source),
    target: mergeSide(traffic?.target, sensors?.target)
  };
};

export const buildTrafficSeriesIdentity = (
  tenantID: string | undefined,
  link: TrafficSeriesRequestLink | null
) => {
  if (!link) return '';
  return [
    tenantID ?? '',
    link.id,
    link.source_device_id ?? '',
    link.source_interface_id ?? '',
    link.telemetry_ifindex_a ?? '',
    link.target_device_id ?? '',
    link.target_interface_id ?? '',
    link.telemetry_ifindex_b ?? ''
  ].join(':');
};

export const safeTrafficSeriesDiagnostic = (value: unknown) => {
  if (typeof value !== 'string') return '';
  return value.trim().slice(0, 320);
};

export const classifyTrafficSeriesPayload = (payload: TrafficSeriesResponse) => {
  const error = safeTrafficSeriesDiagnostic(payload.error);
  if (error) return { kind: 'error' as const, message: error };
  if (!payload.enabled) return { kind: 'disabled' as const, message: '' };
  return { kind: 'enabled' as const, message: '' };
};

export const trafficSeriesResponseHasData = (response: TrafficSeriesResponse | null) =>
  [response?.source, response?.target].some(side =>
    side?.metrics?.some(metric =>
      resolveSeriesBucket(metric) !== null && (metric.points?.length ?? 0) > 0
    )
  );

export const createTrafficSeriesPollCoordinator = (
  run: (signal: AbortSignal) => Promise<void>
) => {
  let activeController: AbortController | null = null;
  let pending = false;
  let stopped = false;

  const poll = () => {
    if (stopped) return;
    if (activeController) {
      pending = true;
      return;
    }

    const controller = new AbortController();
    activeController = controller;
    void run(controller.signal)
      .catch(() => undefined)
      .finally(() => {
        if (activeController !== controller) return;
        activeController = null;
        if (pending && !stopped) {
          pending = false;
          poll();
        }
      });
  };

  const stop = () => {
    stopped = true;
    pending = false;
    activeController?.abort();
    activeController = null;
  };

  return { poll, stop };
};

export const trafficSeriesContentState = ({
  loading,
  error,
  disabled,
  response
}: {
  loading: boolean;
  error: string | null;
  disabled: boolean;
  response: TrafficSeriesResponse | null;
}) => {
  if (error) return 'error' as const;
  if (disabled) return 'disabled' as const;
  if (loading && !response) return 'loading' as const;
  if (!response) return 'idle' as const;
  return trafficSeriesResponseHasData(response) ? 'ready' as const : 'no-data' as const;
};

type Packet = {
  id: number;
  source: [number, number];
  target: [number, number];
  renderHeight?: number;
  direction: PacketDirection;
  status: LinkStatus;
  startTime: number;
  endTime: number;
};

type LoopRing = {
  id: string;
  center: [number, number];
  radius: number;
  status: LinkStatus;
  pps: number;
  path: Array<[number, number]>;
  link?: Link;
  links?: Link[];
};

type LoopPacket = {
  id: number;
  center: [number, number];
  radius: number;
  direction: 1 | -1;
  status: LinkStatus;
  phase: number;
  startTime: number;
  endTime: number;
};

type CoCluster = {
  id: string;
  center: [number, number];
  links: Link[];
};


type CoHubEntry = {
  id: string;
  center: [number, number];
  links: Link[];
  hub?: Hub;
};

export type NetworkMapProps = {
  showHealthy: boolean;
  showBroken: boolean;
  staticMode?: boolean;
  kioskMode?: boolean;
  publicMode?: boolean;
  telemetryClient?: StatusTelemetryClient;
  snapshotPollIntervalMs?: number;
};

export type StatusTelemetryMapViewProps = NetworkMapProps & {
  snapshot: StatusMapSnapshot | null;
  transport: MapTransportState;
  freshness: { inventory: string; telemetry: string; alerting: string } | null;
  telemetryEstimated: boolean;
  loadError?: string | null;
  focusedAlert: StatusAlert | null;
  onFocusAlert: (alertID: string | null) => void;
  onOpenGrafana: (target: { deviceId: string; ifindex?: number; from?: string; to?: string }) => void;
};

const normalizeStatus = (value: unknown): LinkStatus => {
  if (value === LINK_STATUS_WARNING || value === 'warning' || value === 'degraded') return LINK_STATUS_WARNING;
  if (value === LINK_STATUS_MISSING || value === 'unknown' || value === 'unavailable') return LINK_STATUS_MISSING;
  if (value === 'broken' || value === 'down' || value === 0 || value === '0' || value === false) {
    return 'broken';
  }
  return 'healthy';
};

const isProblemStatus = (status: LinkStatus) =>
  status === LINK_STATUS_WARNING || status === 'broken' || status === LINK_STATUS_MISSING;
const isBrokenLikeStatus = (status: LinkStatus) => status === 'broken' || status === LINK_STATUS_MISSING;
const linkStatusSeverity = (status: LinkStatus) => {
  if (status === 'broken') return 4;
  if (status === LINK_STATUS_MISSING) return 3;
  if (status === LINK_STATUS_WARNING) return 2;
  return 1;
};
const aggregateLinkStatus = (links: Link[]) =>
  links.reduce<LinkStatus>((status, link) => (linkStatusSeverity(link.status) > linkStatusSeverity(status) ? link.status : status), 'healthy');
const representativeClusterLink = (links: Link[]) =>
  [...links].sort((a, b) => linkStatusSeverity(b.status) - linkStatusSeverity(a.status) || a.id.localeCompare(b.id))[0];

const parseOptionalNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') return null;
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

const parseOptionalString = (value: unknown): string | null => {
  if (value === null || value === undefined) return null;
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
};

const parseHubNumericKey = (value: unknown): string | null => {
  const text = typeof value === 'number' ? String(value) : parseOptionalString(value);
  if (!text) return null;
  const direct = Number(text);
  if (Number.isInteger(direct) && direct > 0) return String(direct);
  const suffix = text.includes(':') ? text.split(':').pop() : null;
  if (!suffix) return null;
  const parsed = Number(suffix);
  return Number.isInteger(parsed) && parsed > 0 ? String(parsed) : null;
};

const normalizeStatusReason = (raw: any): LinkStatusReason | undefined => {
  if (!raw || typeof raw !== 'object') return undefined;
  const code = parseOptionalString(raw.code);
  const summary = parseOptionalString(raw.summary);
  const details = Array.isArray(raw.details)
    ? raw.details.map((item: unknown) => String(item ?? '').trim()).filter(Boolean)
    : [];
  const sourceOperStatus = parseOptionalNumber(raw.source_oper_status ?? raw.sourceOperStatus);
  const targetOperStatus = parseOptionalNumber(raw.target_oper_status ?? raw.targetOperStatus);
  const sourceOutBps = parseOptionalNumber(raw.source_out_bps ?? raw.sourceOutBps);
  const sourceInBps = parseOptionalNumber(raw.source_in_bps ?? raw.sourceInBps);
  const targetOutBps = parseOptionalNumber(raw.target_out_bps ?? raw.targetOutBps);
  const targetInBps = parseOptionalNumber(raw.target_in_bps ?? raw.targetInBps);
  const asymmetryRatio = parseOptionalNumber(raw.asymmetry_ratio ?? raw.asymmetryRatio);
  const minLoadThresholdBps = parseOptionalNumber(raw.min_load_threshold_bps ?? raw.minLoadThresholdBps);
  const nearZeroThresholdBps = parseOptionalNumber(raw.near_zero_threshold_bps ?? raw.nearZeroThresholdBps);
  const ratioThreshold = parseOptionalNumber(raw.ratio_threshold ?? raw.ratioThreshold);

  if (
    !code &&
    !summary &&
    details.length === 0 &&
    sourceOperStatus === null &&
    targetOperStatus === null &&
    sourceOutBps === null &&
    sourceInBps === null &&
    targetOutBps === null &&
    targetInBps === null &&
    asymmetryRatio === null &&
    minLoadThresholdBps === null &&
    nearZeroThresholdBps === null &&
    ratioThreshold === null
  ) {
    return undefined;
  }
  return {
    code: code ?? undefined,
    summary: summary ?? undefined,
    details: details.length ? details : undefined,
    source_oper_status: sourceOperStatus ?? undefined,
    target_oper_status: targetOperStatus ?? undefined,
    source_out_bps: sourceOutBps ?? undefined,
    source_in_bps: sourceInBps ?? undefined,
    target_out_bps: targetOutBps ?? undefined,
    target_in_bps: targetInBps ?? undefined,
    asymmetry_ratio: asymmetryRatio ?? undefined,
    min_load_threshold_bps: minLoadThresholdBps ?? undefined,
    near_zero_threshold_bps: nearZeroThresholdBps ?? undefined,
    ratio_threshold: ratioThreshold ?? undefined
  };
};

const normalizeMissingTelemetry = (raw: any): MissingTelemetry | undefined => {
  if (!raw || typeof raw !== 'object') return undefined;
  const source = Array.isArray(raw.source)
    ? raw.source.map((item: unknown) => String(item ?? '').trim()).filter(Boolean)
    : [];
  const target = Array.isArray(raw.target)
    ? raw.target.map((item: unknown) => String(item ?? '').trim()).filter(Boolean)
    : [];
  if (!source.length && !target.length) return undefined;
  return {
    source: source.length ? source : undefined,
    target: target.length ? target : undefined
  };
};

const normalizeLinkMember = (raw: any): LinkMember | null => {
  if (!raw || typeof raw !== 'object') return null;
  const sourceDeviceId = parseOptionalString(raw.source_device_id ?? raw.sourceDeviceId ?? raw.a_device_id ?? raw.aDeviceId);
  const sourceInterfaceId = parseOptionalString(raw.source_interface_id ?? raw.sourceInterfaceId ?? raw.a_interface_id ?? raw.aInterfaceId);
  const targetDeviceId = parseOptionalString(raw.target_device_id ?? raw.targetDeviceId ?? raw.z_device_id ?? raw.zDeviceId);
  const targetInterfaceId = parseOptionalString(raw.target_interface_id ?? raw.targetInterfaceId ?? raw.z_interface_id ?? raw.zInterfaceId);
  const sourceInterfaceName = parseOptionalString(raw.source_interface_name ?? raw.sourceInterfaceName ?? raw.a_interface_name ?? raw.aInterfaceName);
  const targetInterfaceName = parseOptionalString(raw.target_interface_name ?? raw.targetInterfaceName ?? raw.z_interface_name ?? raw.zInterfaceName);
  const sourceDeviceName = parseOptionalString(raw.source_device_name ?? raw.sourceDeviceName ?? raw.a_device_name ?? raw.aDeviceName);
  const targetDeviceName = parseOptionalString(raw.target_device_name ?? raw.targetDeviceName ?? raw.z_device_name ?? raw.zDeviceName);
  const rawEvidenceTypes = raw.evidence_types ?? raw.evidenceTypes;
  const capacityBps = parseOptionalNumber(raw.capacity_bps ?? raw.capacityBps);
  const ingressBps = parseOptionalNumber(raw.ingress_bps ?? raw.ingressBps);
  const egressBps = parseOptionalNumber(raw.egress_bps ?? raw.egressBps);
  const ingressUtilizationPct = parseOptionalNumber(raw.ingress_utilization_pct ?? raw.ingressUtilizationPct);
  const egressUtilizationPct = parseOptionalNumber(raw.egress_utilization_pct ?? raw.egressUtilizationPct);
  const evidenceTypes = Array.isArray(rawEvidenceTypes)
    ? rawEvidenceTypes.map((item: unknown) => String(item ?? '').trim()).filter(Boolean)
    : [];
  if (!sourceInterfaceId && !targetInterfaceId && !sourceInterfaceName && !targetInterfaceName) {
    return null;
  }
  return {
    source_device_id: sourceDeviceId ?? undefined,
    source_interface_id: sourceInterfaceId ?? undefined,
    source_device_name: sourceDeviceName ?? undefined,
    source_interface_name: sourceInterfaceName ?? undefined,
    target_device_id: targetDeviceId ?? undefined,
    target_interface_id: targetInterfaceId ?? undefined,
    target_device_name: targetDeviceName ?? undefined,
    target_interface_name: targetInterfaceName ?? undefined,
    evidence_types: evidenceTypes.length ? evidenceTypes : undefined,
    capacity_bps: capacityBps ?? undefined,
    ingress_bps: ingressBps ?? undefined,
    egress_bps: egressBps ?? undefined,
    ingress_utilization_pct: ingressUtilizationPct ?? undefined,
    egress_utilization_pct: egressUtilizationPct ?? undefined
  };
};

const normalizeLinkMembers = (raw: unknown): LinkMember[] | undefined => {
  if (!Array.isArray(raw)) return undefined;
  const members = raw.map(normalizeLinkMember).filter((member): member is LinkMember => member !== null);
  return members.length ? members : undefined;
};

const normalizeLinkType = (value: unknown): string => {
  if (typeof value !== 'string') return 'physical';
  const trimmed = value.trim().toLowerCase();
  return trimmed || 'physical';
};

const endpointMatches = (
  linkDeviceId: string | undefined,
  linkInterfaceId: string | undefined,
  memberDeviceId: string | undefined,
  memberInterfaceId: string | undefined
) => {
  if (!linkInterfaceId || !memberInterfaceId || linkInterfaceId !== memberInterfaceId) return false;
  if (linkDeviceId && memberDeviceId && linkDeviceId !== memberDeviceId) return false;
  return true;
};

const linkMatchesLagMember = (link: Link, member: LinkMember) => {
  const sameDirection =
    endpointMatches(link.source_device_id, link.source_interface_id, member.source_device_id, member.source_interface_id) &&
    endpointMatches(link.target_device_id, link.target_interface_id, member.target_device_id, member.target_interface_id);
  if (sameDirection) return true;
  return (
    endpointMatches(link.source_device_id, link.source_interface_id, member.target_device_id, member.target_interface_id) &&
    endpointMatches(link.target_device_id, link.target_interface_id, member.source_device_id, member.source_interface_id)
  );
};

const suppressLagMemberArcs = (input: Link[]) => {
  const lagParents = input.filter(link => normalizeLinkType(link.link_type) === 'lag' && (link.member_links?.length ?? 0) > 0);
  if (lagParents.length === 0) return input;
  return input.filter(link => {
    if (normalizeLinkType(link.link_type) === 'lag') return true;
    return !lagParents.some(parent => parent.member_links?.some(member => linkMatchesLagMember(link, member)));
  });
};

const normalizeCoCoord = (lat: number | null, lng: number | null) => {
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (lat === 0 && lng === 0) return null;
  return { lat: lat as number, lng: lng as number };
};

const buildDemoLinks = (): Link[] => {
  const chicago = { lat: 41.8781, lng: -87.6298, id: 1001 };
  const detroit = { lat: 42.3314, lng: -83.0458, id: 1002 };
  const lansing = { lat: 42.7325, lng: -84.5555, id: 1003 };
  const now = new Date().toISOString();
  const sample = [
    {
      id: 'demo-1',
      source: [chicago.lng, chicago.lat],
      target: [detroit.lng, detroit.lat],
      source_co_id: chicago.id,
      target_co_id: detroit.id,
      source_co_lat: chicago.lat,
      source_co_lng: chicago.lng,
      target_co_lat: detroit.lat,
      target_co_lng: detroit.lng,
      status: 'healthy',
      bandwidth_mbps: 1000,
      updated_at: now
    },
    {
      id: 'demo-2',
      source: [detroit.lng, detroit.lat],
      target: [lansing.lng, lansing.lat],
      source_co_id: detroit.id,
      target_co_id: lansing.id,
      source_co_lat: detroit.lat,
      source_co_lng: detroit.lng,
      target_co_lat: lansing.lat,
      target_co_lng: lansing.lng,
      status: 'broken',
      bandwidth_mbps: 500,
      updated_at: now
    },
    {
      id: 'demo-3',
      source: [lansing.lng, lansing.lat],
      target: [chicago.lng, chicago.lat],
      source_co_id: lansing.id,
      target_co_id: chicago.id,
      source_co_lat: lansing.lat,
      source_co_lng: lansing.lng,
      target_co_lat: chicago.lat,
      target_co_lng: chicago.lng,
      status: 'healthy',
      bandwidth_mbps: 2000,
      updated_at: now
    },
    {
      id: 'demo-loop',
      source: [lansing.lng, lansing.lat],
      target: [lansing.lng, lansing.lat],
      source_co_id: lansing.id,
      target_co_id: lansing.id,
      source_co_lat: lansing.lat,
      source_co_lng: lansing.lng,
      target_co_lat: lansing.lat,
      target_co_lng: lansing.lng,
      status: 'healthy',
      bandwidth_mbps: 250,
      updated_at: now
    }
  ];
  return sample.map(normalizeLink).filter(Boolean) as Link[];
};

const randomNormal = (): number => {
  let u = 0;
  let v = 0;
  while (u === 0) u = Math.random();
  while (v === 0) v = Math.random();
  return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v);
};

const samplePoisson = (lambda: number): number => {
  if (lambda <= 0) return 0;
  if (lambda < 30) {
    const threshold = Math.exp(-lambda);
    let k = 0;
    let p = 1;
    while (p > threshold) {
      k += 1;
      p *= Math.random();
    }
    return Math.max(0, k - 1);
  }
  const normal = randomNormal() * Math.sqrt(lambda) + lambda;
  return Math.max(0, Math.round(normal));
};

const formatMbps = (bps: number | null | undefined) => {
  if (!Number.isFinite(bps) || !bps || bps <= 0) return '—';
  const mbps = bps / 1_000_000;
  if (!Number.isFinite(mbps) || mbps <= 0) return '—';
  return mbps >= 10 ? `${mbps.toFixed(1)} Mbps` : `${mbps.toFixed(2)} Mbps`;
};

const formatGbps = (bps: number | null | undefined) => {
  if (!Number.isFinite(bps) || !bps || bps <= 0) return '—';
  const gbps = bps / 1_000_000_000;
  if (!Number.isFinite(gbps) || gbps <= 0) return '—';
  return `${gbps.toFixed(2)} Gbps`;
};

const formatCapacityMbps = (mbps: number | null | undefined) => {
  if (!Number.isFinite(mbps) || !mbps || mbps <= 0) return '—';
  return mbps >= 10 ? `${mbps.toFixed(1)} Mbps` : `${mbps.toFixed(2)} Mbps`;
};

const formatPps = (pps: number | null | undefined) => {
  if (!Number.isFinite(pps) || !pps || pps <= 0) return '—';
  if (pps >= 1000) return `${(pps / 1000).toFixed(1)}k pps`;
  return `${pps.toFixed(1)} pps`;
};

const formatOperStatus = (status: number | null | undefined) => {
  if (!Number.isFinite(status) || !status || status <= 0) return 'unknown';
  const numeric = Number(status);
  const labels: Record<number, string> = {
    1: 'up',
    2: 'down',
    3: 'testing',
    4: 'unknown',
    5: 'dormant',
    6: 'notPresent',
    7: 'lowerLayerDown'
  };
  const label = labels[numeric] ?? 'unknown';
  return `${numeric} (${label})`;
};

const formatHostOid = (host?: string | null, oid?: number | null) => {
  const cleanHost = host ?? '';
  const cleanOid = Number.isFinite(oid) ? oid : null;
  if (!cleanHost && !cleanOid) return '—';
  if (cleanHost && cleanOid) return `${cleanHost} · ${cleanOid}`;
  return cleanHost || String(cleanOid ?? '—');
};

const formatHostInterfaceName = (host?: string | null, ifaceName?: string | null) => {
  const cleanHost = (host ?? '').trim();
  const cleanIface = (ifaceName ?? '').trim();
  if (cleanHost && cleanIface) return `${cleanHost} · ${cleanIface}`;
  return cleanHost || cleanIface || '—';
};

const formatMissingTelemetryLabel = (value: string) => {
  const normalized = value.trim().toLowerCase();
  if (normalized === 'host') return 'host';
  if (normalized === 'ifindex') return 'ifIndex';
  if (normalized === 'oper_status') return 'oper status';
  return normalized.replace(/_/g, ' ');
};

const normalizeMissingTelemetrySide = (values?: string[]) =>
  (values ?? []).map(value => value.trim()).filter(Boolean);

const truncateEnd = (value: string, maxLength: number) => {
  if (value.length <= maxLength) return value;
  const slice = Math.max(1, maxLength - 1);
  return `${value.slice(0, slice)}…`;
};

const formatHostInterfaceCompact = (
  host?: string | null,
  oid?: number | null,
  ifaceName?: string | null
) => {
  const cleanHost = (host ?? '').trim();
  const cleanIfaceName = (ifaceName ?? '').trim();
  const hasHost = cleanHost.length > 0;
  const hasOid = Number.isFinite(oid);
  const iface = cleanIfaceName || (hasOid ? `ifIndex ${oid}` : '');
  const full = hasHost && iface ? `${cleanHost} · ${iface}` : hasHost ? cleanHost : iface;
  return { host: cleanHost || '—', iface: iface || '—', full: full || '—', empty: !full };
};

const formatLagMemberEndpoint = (
  deviceName?: string | null,
  interfaceName?: string | null,
  deviceId?: string | null,
  interfaceId?: string | null
) => {
  const cleanDevice = (deviceName ?? '').trim();
  const cleanInterface = (interfaceName ?? '').trim();
  const fallbackDevice = (deviceId ?? '').trim();
  const fallbackInterface = (interfaceId ?? '').trim();
  const device = cleanDevice || (fallbackDevice ? truncateEnd(fallbackDevice, 8) : '');
  const iface = cleanInterface || (fallbackInterface ? truncateEnd(fallbackInterface, 8) : '');
  if (device && iface) return `${device} · ${iface}`;
  return device || iface || '—';
};

const formatSeriesValue = (value: number | null | undefined, unit?: string) => {
  if (!Number.isFinite(value) || value === null) return '—';
  const normalizedUnit = (unit ?? '').toLowerCase();
  if (normalizedUnit.includes('bps')) {
    return formatMbps(value);
  }
  if (normalizedUnit.includes('pps')) {
    return formatPps(value);
  }
  if (normalizedUnit.includes('error') || normalizedUnit.includes('discard')) {
    return `${Math.round(value)} ${unit}`;
  }
  if (normalizedUnit.includes('count')) {
    return `${Math.round(value)}${unit ? ` ${unit}` : ''}`;
  }
  return `${value.toFixed(2)}${unit ? ` ${unit}` : ''}`;
};

const formatRangeLabel = (seconds: number) => {
  if (!Number.isFinite(seconds) || seconds <= 0) return '—';
  const minute = 60;
  const hour = 60 * minute;
  const day = 24 * hour;
  const year = 365 * day;
  if (seconds >= year && seconds % year === 0) return `${Math.round(seconds / year)}y`;
  if (seconds >= day && seconds % day === 0) return `${Math.round(seconds / day)}d`;
  if (seconds >= hour) return `${Math.round(seconds / hour)}h`;
  return `${Math.round(seconds / minute)}m`;
};

const formatSeriesTick = (value: number, rangeSeconds?: number) => {
  if (!Number.isFinite(value)) return '';
  const date = new Date(value);
  if (rangeSeconds && rangeSeconds >= 24 * 60 * 60) {
    const longRange = rangeSeconds >= 180 * 24 * 60 * 60;
    return new Intl.DateTimeFormat(undefined, longRange
      ? { month: 'short', year: '2-digit' }
      : { month: 'short', day: '2-digit' }
    ).format(date);
  }
  return new Intl.DateTimeFormat(undefined, { hour: '2-digit', minute: '2-digit' }).format(date);
};

const formatSeriesTooltipTime = (value: number) => {
  if (!Number.isFinite(value)) return '—';
  const date = new Date(value);
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  }).format(date);
};

const formatUtilization = (value: number | null | undefined) => {
  if (!Number.isFinite(value)) return '—';
  return `${(Math.max(0, value as number) * 100).toFixed(0)}%`;
};

const formatAge = (valueMs: number | undefined) => {
  if (!Number.isFinite(valueMs)) return '—';
  const seconds = Math.max(0, Math.round((Date.now() - (valueMs as number)) / 1000));
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  return `${days}d ago`;
};

const scaleAlpha = (alpha: number, scale: number) => Math.max(0, Math.min(255, Math.round(alpha * scale)));
const toRgba = (color: [number, number, number], alpha: number) =>
  `rgba(${color[0]}, ${color[1]}, ${color[2]}, ${alpha})`;
const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

const getFadeScale = (updatedAtMs: number | undefined, nowMs: number) => {
  if (!Number.isFinite(updatedAtMs)) return 1;
  const ageSec = (nowMs - (updatedAtMs as number)) / 1000;
  if (!Number.isFinite(ageSec) || ageSec <= LINK_FADE_START_SEC) return 1;
  if (ageSec >= LINK_FADE_END_SEC) return LINK_FADE_MIN_ALPHA;
  const t = (ageSec - LINK_FADE_START_SEC) / (LINK_FADE_END_SEC - LINK_FADE_START_SEC);
  return 1 - t * (1 - LINK_FADE_MIN_ALPHA);
};

const getUtilColor = (value: number | null | undefined): [number, number, number] => {
  if (!Number.isFinite(value)) return LINK_COLOR_HEALTHY_LOW;
  const util = value as number;
  if (util < LINK_UTIL_WARN) return LINK_COLOR_HEALTHY_LOW;
  if (util < LINK_UTIL_CRIT) return LINK_COLOR_HEALTHY_WARN;
  return LINK_COLOR_HEALTHY_HIGH;
};



const getTrafficAgeTone = (updatedAtMs: number | undefined, trafficEnabled: boolean) => {
  if (!trafficEnabled || !Number.isFinite(updatedAtMs)) return 'text-muted-foreground';
  const ageMs = Date.now() - (updatedAtMs as number);
  if (!Number.isFinite(ageMs) || ageMs < 0) return 'text-muted-foreground';
  if (ageMs >= TRAFFIC_STALE_CRIT_MS) return 'text-destructive';
  if (ageMs >= TRAFFIC_STALE_WARN_MS) return 'text-amber-400';
  return 'text-emerald-400';
};

const getSeriesStepSeconds = (rangeSeconds: number) => {
  if (!Number.isFinite(rangeSeconds) || rangeSeconds <= 0) return 30;
  const targetPoints = 240;
  let step = Math.round(rangeSeconds / targetPoints);
  if (step < 30) step = 30;
  if (step > 24 * 60 * 60) step = 24 * 60 * 60;
  return step;
};

const buildSeriesData = (metrics: TrafficSeriesMetric[], nowMs: number | undefined, rangeSeconds: number) => {
  const seriesPoints = metrics.map(metric => ({
    name: metric.name,
    points: [...(metric.points ?? [])].sort((a, b) => a.ts - b.ts)
  }));
  if (!seriesPoints.length) return [];
  let latestTime = 0;
  seriesPoints.forEach(metric => {
    const last = metric.points[metric.points.length - 1];
    if (last) {
      const time = last.ts * 1000;
      if (time > latestTime) latestTime = time;
    }
  });
  if (!latestTime) return [];
  const effectiveNow = Number.isFinite(nowMs) ? Math.max(latestTime, nowMs as number) : latestTime;
  const rangeMs = rangeSeconds * 1000;
  const startTime = effectiveNow - rangeMs;
  const stepMs = getSeriesStepSeconds(rangeSeconds) * 1000;

  const data: Array<Record<string, number>> = [];
  const cursors = new Map<string, { index: number; last?: number; lastTime?: number }>();
  seriesPoints.forEach(metric => {
    cursors.set(metric.name, { index: 0 });
  });

  for (let time = startTime; time <= effectiveNow; time += stepMs) {
    const row: Record<string, number> = { time };
    seriesPoints.forEach(metric => {
      const cursor = cursors.get(metric.name);
      if (!cursor) return;
      while (cursor.index < metric.points.length && metric.points[cursor.index].ts * 1000 <= time) {
        cursor.last = metric.points[cursor.index].value;
        cursor.lastTime = metric.points[cursor.index].ts * 1000;
        cursor.index += 1;
      }
      if (cursor.last !== undefined && cursor.lastTime !== undefined && cursor.lastTime <= time) {
        row[metric.name] = cursor.last;
      }
    });
    data.push(row);
  }

  return data;
};

const getSeriesMaxTime = (metrics: TrafficSeriesMetric[]) => {
  let maxTime = 0;
  metrics.forEach(metric => {
    metric.points?.forEach(point => {
      const time = point.ts * 1000;
      if (time > maxTime) {
        maxTime = time;
      }
    });
  });
  return maxTime || null;
};

const stripSeriesSidePrefix = (name: string) => {
  if (name.startsWith('a_') || name.startsWith('z_')) {
    return name.slice(2);
  }
  return name;
};

const parseSeriesMetricKey = (name: string) => {
  const normalized = name.toLowerCase();
  let side: 'a' | 'z' | 'unknown' = 'unknown';
  let trimmed = normalized;
  if (normalized.startsWith('a_')) {
    side = 'a';
    trimmed = normalized.slice(2);
  } else if (normalized.startsWith('z_')) {
    side = 'z';
    trimmed = normalized.slice(2);
  }
  let direction: 'in' | 'out' | 'other' = 'other';
  if (trimmed.startsWith('in_')) {
    direction = 'in';
  } else if (trimmed.startsWith('out_')) {
    direction = 'out';
  }
  const base = trimmed.replace(/^in_/, '').replace(/^out_/, '');
  return { side, direction, base };
};

const resolveSeriesBucket = (metric: TrafficSeriesMetric): SeriesBucketKey | null => {
  const name = stripSeriesSidePrefix(metric.name.toLowerCase());
  const unit = metric.unit?.toLowerCase() ?? '';
  if (name.includes('bps') || unit === 'bps') return 'bandwidth';
  if (name.includes('packet') || unit === 'pps') return 'packets';
  if (name.includes('error') || name.includes('discard') || name.includes('crc')) return 'errors';
  if (name.includes('temp') || name.includes('temperature') || name.includes('thermal')) return 'temperature';
  if (
    name.includes('optic') ||
    name.includes('optical') ||
    name.includes('light') ||
    name.includes('laser') ||
    name.includes('bias') ||
    name.includes('power') ||
    name.includes('dom') ||
    unit.includes('dbm')
  ) {
    return 'optics';
  }
  return null;
};

const getSeriesBucketUnitLabel = (bucketKey: SeriesBucketKey, metrics: TrafficSeriesMetric[]) => {
  const units = new Set(
    metrics
      .map(metric => metric.unit?.trim())
      .filter((value): value is string => Boolean(value))
  );
  if (units.size === 1) {
    return Array.from(units.values())[0];
  }
  if (bucketKey === 'errors') return 'count';
  if (bucketKey === 'bandwidth') return 'bps';
  if (bucketKey === 'packets') return 'pps';
  return '';
};

const getMetricStroke = (name: string) => {
  const parsed = parseSeriesMetricKey(name);
  const palette =
    parsed.side === 'z'
      ? {
          in: 'var(--color-secondary)',
          out: 'var(--color-warning)',
          other: 'var(--color-secondary)'
        }
      : {
          in: 'var(--color-success)',
          out: 'var(--color-info)',
          other: 'var(--color-success)'
        };
  return palette[parsed.direction] ?? palette.other;
};

const sortSeriesMetrics = (metrics: TrafficSeriesMetric[]) => {
  const sideOrder = { a: 0, z: 1, unknown: 2 } as const;
  const directionOrder = { in: 0, out: 1, other: 2 } as const;
  return [...metrics].sort((a, b) => {
    const aParsed = parseSeriesMetricKey(a.name);
    const bParsed = parseSeriesMetricKey(b.name);
    if (sideOrder[aParsed.side] !== sideOrder[bParsed.side]) {
      return sideOrder[aParsed.side] - sideOrder[bParsed.side];
    }
    if (directionOrder[aParsed.direction] !== directionOrder[bParsed.direction]) {
      return directionOrder[aParsed.direction] - directionOrder[bParsed.direction];
    }
    return a.name.localeCompare(b.name);
  });
};

const groupSeriesMetrics = (metrics: TrafficSeriesMetric[]) => {
  const groups = new Map<SeriesBucketKey, TrafficSeriesMetric[]>();
  metrics.forEach(metric => {
    const bucket = resolveSeriesBucket(metric);
    if (!bucket) return;
    const entry = groups.get(bucket) ?? [];
    entry.push(metric);
    groups.set(bucket, entry);
  });
  return SERIES_BUCKETS.map(bucket => ({
    key: bucket.key,
    label: bucket.label,
    metrics: sortSeriesMetrics(groups.get(bucket.key) ?? [])
  }));
};

const getArcWidthForLink = (link: Link, allLinks?: Link[]): number => {
  const capacity = getCapacityMbps(link, allLinks);
  if (!capacity) return ARC_WIDTH_MIN;
  const min = ARC_WIDTH_SPEED_MIN_MBPS;
  const max = ARC_WIDTH_SPEED_MAX_MBPS;
  const clamped = Math.min(Math.max(capacity, min), max);
  const minLog = Math.log10(min);
  const maxLog = Math.log10(max);
  const denom = maxLog - minLog;
  if (denom <= 0) return ARC_WIDTH;
  let ratio = (Math.log10(clamped) - minLog) / denom;
  if (!Number.isFinite(ratio)) ratio = 0;
  ratio = Math.max(0, Math.min(1, ratio));
  const scaled = Math.pow(ratio, ARC_WIDTH_SPEED_EXPONENT);
  return ARC_WIDTH_MIN + scaled * (ARC_WIDTH_MAX - ARC_WIDTH_MIN);
};

const getArcPipeWidthForLink = (link: Link, allLinks?: Link[]): number => {
  const base = getArcWidthForLink(link, allLinks);
  const denom = ARC_WIDTH_MAX - ARC_WIDTH_MIN;
  if (denom <= 0) return ARC_PIPE_WIDTH_MIN;
  let ratio = (base - ARC_WIDTH_MIN) / denom;
  if (!Number.isFinite(ratio)) ratio = 0;
  ratio = Math.max(0, Math.min(1, ratio));
  return ARC_PIPE_WIDTH_MIN + ratio * (ARC_PIPE_WIDTH_MAX - ARC_PIPE_WIDTH_MIN);
};

const toRadians = (deg: number) => (deg * Math.PI) / 180;
const toDegrees = (rad: number) => (rad * 180) / Math.PI;

const greatCircleInterpolate = (
  start: [number, number],
  end: [number, number],
  fraction: number
): [number, number] => {
  const [lon1, lat1] = start.map(toRadians) as [number, number];
  const [lon2, lat2] = end.map(toRadians) as [number, number];
  const sinLat1 = Math.sin(lat1);
  const sinLat2 = Math.sin(lat2);
  const cosLat1 = Math.cos(lat1);
  const cosLat2 = Math.cos(lat2);
  const deltaLon = lon2 - lon1;
  const sinDeltaLat = Math.sin((lat2 - lat1) / 2);
  const sinDeltaLon = Math.sin(deltaLon / 2);
  const a = sinDeltaLat * sinDeltaLat + cosLat1 * cosLat2 * sinDeltaLon * sinDeltaLon;
  const delta = 2 * Math.asin(Math.min(1, Math.sqrt(a)));
  if (delta === 0) return start;
  const sinDelta = Math.sin(delta);
  const A = Math.sin((1 - fraction) * delta) / sinDelta;
  const B = Math.sin(fraction * delta) / sinDelta;
  const x = A * cosLat1 * Math.cos(lon1) + B * cosLat2 * Math.cos(lon2);
  const y = A * cosLat1 * Math.sin(lon1) + B * cosLat2 * Math.sin(lon2);
  const z = A * sinLat1 + B * sinLat2;
  const lat = Math.atan2(z, Math.sqrt(x * x + y * y));
  const lon = Math.atan2(y, x);
  return [toDegrees(lon), toDegrees(lat)];
};

const haversineDistanceMeters = (start: [number, number], end: [number, number]) => {
  const [lon1, lat1] = start.map(toRadians) as [number, number];
  const [lon2, lat2] = end.map(toRadians) as [number, number];
  const dLat = lat2 - lat1;
  const dLon = lon2 - lon1;
  const sinLat = Math.sin(dLat / 2);
  const sinLon = Math.sin(dLon / 2);
  const a = sinLat * sinLat + Math.cos(lat1) * Math.cos(lat2) * sinLon * sinLon;
  return 2 * EARTH_RADIUS_METERS * Math.asin(Math.min(1, Math.sqrt(a)));
};

const getFocusMaxZoom = (distanceMeters: number) => {
  if (!Number.isFinite(distanceMeters) || distanceMeters <= 0) return 10;
  if (distanceMeters < CO_DISTANCE_METERS) return 19.5;
  if (distanceMeters < 200) return 18.5;
  if (distanceMeters < 1000) return 16;
  if (distanceMeters < 5000) return 14.5;
  if (distanceMeters < 20000) return 13;
  if (distanceMeters < 100000) return 11;
  if (distanceMeters < 500000) return 9;
  return 7;
};

const getFocusPadding = (distanceMeters: number, compact: boolean) => {
  if (!Number.isFinite(distanceMeters) || distanceMeters <= 0) return compact ? 60 : 100;
  if (distanceMeters < CO_DISTANCE_METERS) return compact ? 40 : 60;
  if (distanceMeters < 1000) return compact ? 60 : 100;
  if (distanceMeters < 5000) return compact ? 72 : 120;
  if (distanceMeters < 50000) return compact ? 84 : 140;
  return compact ? 96 : 160;
};

const offsetByMeters = (center: [number, number], radiusMeters: number, angleRad: number): [number, number] => {
  const [lng, lat] = center;
  const latRad = toRadians(lat);
  const metersPerDegLat = 111320;
  const metersPerDegLon = Math.max(1e-6, Math.cos(latRad) * 111320);
  const dLat = (Math.sin(angleRad) * radiusMeters) / metersPerDegLat;
  const dLon = (Math.cos(angleRad) * radiusMeters) / metersPerDegLon;
  return [lng + dLon, lat + dLat];
};

const buildCirclePath = (center: [number, number], radiusMeters: number, segments: number) => {
  const path: Array<[number, number]> = [];
  const steps = Math.max(6, segments);
  for (let i = 0; i <= steps; i += 1) {
    const angle = (i / steps) * Math.PI * 2;
    path.push(offsetByMeters(center, radiusMeters, angle));
  }
  return path;
};


const coordKey = (coord: [number, number]) => `${coord[0].toFixed(5)},${coord[1].toFixed(5)}`;

const pairKey = (source: [number, number], target: [number, number]) => {
  const a = coordKey(source);
  const b = coordKey(target);
  return a < b ? `${a}|${b}` : `${b}|${a}`;
};

const compareCoords = (a: [number, number], b: [number, number]) => {
  if (a[0] !== b[0]) return a[0] - b[0];
  return a[1] - b[1];
};

const toArcPosition = (coord: [number, number]) => [coord[0], coord[1], 0] as [number, number, number];

const resolveCoCenter = (
  link: Link,
  hubs?: Map<string, CoHubEntry>
): { id: string; center: [number, number] } | null => {
  const sourceId = link.source_co_id;
  const targetId = link.target_co_id;
  if (!sourceId || !targetId || sourceId !== targetId) return null;
  const id = String(sourceId);
  if (hubs?.has(id)) {
    return { id, center: hubs.get(id)?.center ?? link.source };
  }
  const coord =
    normalizeCoCoord(link.source_co_lat ?? null, link.source_co_lng ?? null) ??
    normalizeCoCoord(link.target_co_lat ?? null, link.target_co_lng ?? null);
  if (!coord) return null;
  return { id, center: [coord.lng, coord.lat] };
};

const resolveCoCoord = (lat?: number, lng?: number): [number, number] | null => {
  const coord = normalizeCoCoord(lat ?? null, lng ?? null);
  if (!coord) return null;
  return [coord.lng, coord.lat];
};

const normalizeLink = (raw: any): Link | null => {
  if (!raw || typeof raw !== 'object') return null;
  const id = String(raw.id ?? '');
  if (!id) return null;
  const status = normalizeStatus(raw.status);
  const bandwidth = Number(raw.bandwidth_mbps ?? 1) || 1;
  const updatedAt = String(raw.updated_at ?? new Date().toISOString());
  const updatedAtMs = Number.isFinite(Date.parse(updatedAt)) ? Date.parse(updatedAt) : undefined;
  const telemetryHostA = parseOptionalString(raw.telemetry_host_a ?? raw.zabbix_host_a ?? raw.zabbixHostA);
  const telemetryHostB = parseOptionalString(raw.telemetry_host_b ?? raw.zabbix_host_b ?? raw.zabbixHostB);
  const telemetryIfIndexA = parseOptionalNumber(raw.telemetry_ifindex_a ?? raw.zabbix_oid_a ?? raw.zabbixOidA);
  const telemetryIfIndexB = parseOptionalNumber(raw.telemetry_ifindex_b ?? raw.zabbix_oid_b ?? raw.zabbixOidB);
  const telemetryIfNameA = parseOptionalString(
    raw.telemetry_ifname_a ??
      raw.telemetry_if_name_a ??
      raw.zabbix_ifname_a ??
      raw.zabbix_if_name_a ??
      raw.zabbix_ifnameA ??
      raw.zabbixIfnameA
  );
  const telemetryIfNameB = parseOptionalString(
    raw.telemetry_ifname_b ??
      raw.telemetry_if_name_b ??
      raw.zabbix_ifname_b ??
      raw.zabbix_if_name_b ??
      raw.zabbix_ifnameB ??
      raw.zabbixIfnameB
  );
  const missingTelemetry = normalizeMissingTelemetry(raw.missing_telemetry ?? raw.missingTelemetry);
  const statusReason = normalizeStatusReason(raw.status_reason ?? raw.statusReason);
  const memberLinks = normalizeLinkMembers(raw.member_links ?? raw.memberLinks);
  const linkType = normalizeLinkType(raw.link_type ?? raw.linkType);
  const topologyStatus = parseOptionalString(raw.topology_status ?? raw.topologyStatus);
  const topologyReason = parseOptionalString(raw.topology_reason ?? raw.topologyReason);
  const topologyConfidence = parseOptionalNumber(raw.topology_confidence ?? raw.topologyConfidence);
  const rawTopologyEvidenceTypes = raw.topology_evidence_types ?? raw.topologyEvidenceTypes;
  const topologyEvidenceTypes = Array.isArray(rawTopologyEvidenceTypes)
    ? rawTopologyEvidenceTypes.map((item: unknown) => String(item ?? '').trim()).filter(Boolean)
    : [];
  const ingressUtilizationPct = parseOptionalNumber(raw.ingress_utilization_pct ?? raw.ingressUtilizationPct);
  const egressUtilizationPct = parseOptionalNumber(raw.egress_utilization_pct ?? raw.egressUtilizationPct);
  const sourceCoId = parseOptionalNumber(raw.source_co_id ?? raw.sourceCoId);
  const targetCoId = parseOptionalNumber(raw.target_co_id ?? raw.targetCoId);
  const sourceCoLat = parseOptionalNumber(raw.source_co_lat ?? raw.sourceCoLat);
  const sourceCoLng = parseOptionalNumber(raw.source_co_lng ?? raw.sourceCoLng);
  const targetCoLat = parseOptionalNumber(raw.target_co_lat ?? raw.targetCoLat);
  const targetCoLng = parseOptionalNumber(raw.target_co_lng ?? raw.targetCoLng);
  const normalizedSourceCoId = sourceCoId && sourceCoId > 0 ? sourceCoId : null;
  const normalizedTargetCoId = targetCoId && targetCoId > 0 ? targetCoId : null;
  const sourceCoCoord = normalizeCoCoord(sourceCoLat, sourceCoLng);
  const targetCoCoord = normalizeCoCoord(targetCoLat, targetCoLng);
  let source = Array.isArray(raw.source)
    ? raw.source
    : raw.source && typeof raw.source === 'object'
      ? [Number(raw.source.lng), Number(raw.source.lat)]
      : null;
  let target = Array.isArray(raw.target)
    ? raw.target
    : raw.target && typeof raw.target === 'object'
      ? [Number(raw.target.lng), Number(raw.target.lat)]
      : null;
  if (sourceCoCoord) {
    source = [sourceCoCoord.lng, sourceCoCoord.lat];
  }
  if (targetCoCoord) {
    target = [targetCoCoord.lng, targetCoCoord.lat];
  }
  if (!source || !target || Number.isNaN(source[0]) || Number.isNaN(source[1]) || Number.isNaN(target[0]) || Number.isNaN(target[1])) {
    return null;
  }
  return {
    id,
    source: [source[0], source[1]],
    target: [target[0], target[1]],
    status,
    status_reason: statusReason,
    missing_telemetry: missingTelemetry,
    bandwidth_mbps: bandwidth,
    updated_at: updatedAt,
    updated_at_ms: updatedAtMs,
    source_co_id: normalizedSourceCoId ?? undefined,
    target_co_id: normalizedTargetCoId ?? undefined,
    source_co_lat: sourceCoCoord?.lat,
    source_co_lng: sourceCoCoord?.lng,
    target_co_lat: targetCoCoord?.lat,
    target_co_lng: targetCoCoord?.lng,
    source_device_id: parseOptionalString(raw.source_device_id ?? raw.sourceDeviceId) ?? undefined,
    target_device_id: parseOptionalString(raw.target_device_id ?? raw.targetDeviceId) ?? undefined,
    source_interface_id: parseOptionalString(raw.source_interface_id ?? raw.sourceInterfaceId) ?? undefined,
    target_interface_id: parseOptionalString(raw.target_interface_id ?? raw.targetInterfaceId) ?? undefined,
    link_type: linkType,
    topology_status: topologyStatus ?? undefined,
    topology_reason: topologyReason ?? undefined,
    topology_confidence: topologyConfidence ?? undefined,
    topology_evidence_types: topologyEvidenceTypes.length ? topologyEvidenceTypes : undefined,
    member_links: memberLinks,
    ingress_utilization_pct: ingressUtilizationPct ?? undefined,
    egress_utilization_pct: egressUtilizationPct ?? undefined,
    telemetry_host_a: telemetryHostA ?? undefined,
    telemetry_host_b: telemetryHostB ?? undefined,
    telemetry_ifindex_a: telemetryIfIndexA ?? undefined,
    telemetry_ifindex_b: telemetryIfIndexB ?? undefined,
    telemetry_ifname_a: telemetryIfNameA ?? undefined,
    telemetry_ifname_b: telemetryIfNameB ?? undefined
  };
};

const normalizeHub = (raw: any): Hub | null => {
  if (!raw || typeof raw !== 'object') return null;
  const id = parseOptionalString(raw.id);
  if (!id) return null;
  const location = raw.location;
  const lat = location && typeof location === 'object' ? parseOptionalNumber(location.lat) : null;
  const lng = location && typeof location === 'object' ? parseOptionalNumber(location.lng) : null;
  if (lat === null || lng === null) return null;
  const coord = normalizeCoCoord(lat, lng);
  if (!coord) return null;
  return {
    id,
    external_id: parseOptionalString(raw.external_id ?? raw.externalId) ?? undefined,
    name: parseOptionalString(raw.name) ?? undefined,
    location: [coord.lng, coord.lat],
    location_source: parseOptionalString(raw.location_source ?? raw.locationSource) ?? undefined
  };
};

// The service projection is authoritative: only explicitly configured tenant links
// become arcs. Device inventory and alert membership never synthesize topology.
export const mapSnapshotLink = (link: MapLink): Link | null =>
  normalizeLink(toLegacyMapLinkInput(link));

export type SnapshotLinkProjectionCache = Map<string, { source: MapLink; projected: Link | null }>;

export const reconcileSnapshotLinkProjection = (
  incoming: MapLink[],
  previous: SnapshotLinkProjectionCache
) => {
  const cache: SnapshotLinkProjectionCache = new Map();
  const links: Link[] = [];
  incoming.forEach(source => {
    const cached = previous.get(source.id);
    const projected = cached?.source === source ? cached.projected : mapSnapshotLink(source);
    cache.set(source.id, { source, projected });
    if (projected) links.push(projected);
  });
  return { cache, links };
};

const getDirectCapacityMbps = (link: Link): number | null => {
  const mbps = Number(link.bandwidth_mbps);
  if (!Number.isFinite(mbps) || mbps <= 1) return null;
  return mbps;
};

const getLagMemberRuntimeLinks = (link: Link, allLinks?: Link[]): Link[] => {
  if (normalizeLinkType(link.link_type) !== 'lag' || !link.member_links?.length || !allLinks?.length) return [];
  const members = allLinks.filter(candidate => {
    if (candidate.id === link.id || normalizeLinkType(candidate.link_type) === 'lag') return false;
    return link.member_links?.some(member => linkMatchesLagMember(candidate, member));
  });
  const seen = new Set<string>();
  return members.filter(member => {
    if (seen.has(member.id)) return false;
    seen.add(member.id);
    return true;
  });
};

const getLagMemberDeclaredCapacityMbps = (link: Link): number | null => {
  const capacity = (link.member_links ?? []).reduce((total, member) => {
    const bps = Number(member.capacity_bps);
    return Number.isFinite(bps) && bps > 0 ? total + bps / 1_000_000 : total;
  }, 0);
  return capacity > 0 ? capacity : null;
};

const getCapacityMbps = (link: Link, allLinks?: Link[]): number | null => {
  const direct = getDirectCapacityMbps(link);
  if (direct !== null) return direct;
  const memberCapacity = getLagMemberRuntimeLinks(link, allLinks).reduce((total, member) => {
    const capacity = getDirectCapacityMbps(member);
    return capacity !== null ? total + capacity : total;
  }, 0);
  if (memberCapacity > 0) return memberCapacity;
  return getLagMemberDeclaredCapacityMbps(link);
};

const getStateUtilization = (link: Link): number | null => {
  const values = [utilizationPctToRatio(link.ingress_utilization_pct), utilizationPctToRatio(link.egress_utilization_pct)].filter(
    (value): value is number => value !== null
  );
  if (!values.length) return null;
  return Math.max(...values);
};

const getDeclaredLagMemberUtilization = (link: Link): number | null => {
  const stats = (link.member_links ?? [])
    .map(member => {
      const capacity = Number(member.capacity_bps);
      const utilization = Math.max(
        utilizationPctToRatio(member.ingress_utilization_pct) ?? -1,
        utilizationPctToRatio(member.egress_utilization_pct) ?? -1
      );
      if (utilization >= 0) {
        return { capacity, utilization };
      }
      const ingressBps = Number(member.ingress_bps);
      const egressBps = Number(member.egress_bps);
      if (!Number.isFinite(capacity) || capacity <= 0) {
        return { capacity, utilization: -1 };
      }
      const bps = Math.max(Number.isFinite(ingressBps) ? ingressBps : 0, Number.isFinite(egressBps) ? egressBps : 0);
      return { capacity, utilization: bps > 0 ? bps / capacity : -1 };
    })
    .filter(stat => stat.utilization >= 0);
  if (!stats.length) return null;
  const weighted = stats.reduce(
    (acc, stat) => {
      if (Number.isFinite(stat.capacity) && stat.capacity > 0) {
        acc.capacity += stat.capacity;
        acc.used += stat.capacity * stat.utilization;
      }
      return acc;
    },
    { capacity: 0, used: 0 }
  );
  if (weighted.capacity > 0) return Math.max(0, weighted.used / weighted.capacity);
  return Math.max(...stats.map(stat => stat.utilization));
};

const getLinkUtilization = (link: Link, links: Link[]): number | null => {
  if (!link) return null;
  const stateUtil = getStateUtilization(link);
  if (Number.isFinite(stateUtil)) return stateUtil as number;
  if (normalizeLinkType(link.link_type) === 'lag') {
    return getDeclaredLagMemberUtilization(link);
  }
  return null;
};

export const StatusTelemetryMapView = ({
  showHealthy: initialShowHealthy,
  showBroken: initialShowBroken,
  staticMode = false,
  kioskMode = false,
  publicMode = false,
  snapshot,
  transport,
  freshness,
  telemetryEstimated,
  loadError,
  focusedAlert,
  onFocusAlert,
  onOpenGrafana
}: StatusTelemetryMapViewProps) => {
  const [baseStyle, setBaseStyle] = useState<Record<string, any> | null>(null);
  const [styleError, setStyleError] = useState<string | null>(null);
  const [basemapAvailable, setBasemapAvailable] = useState(false);
  const [mapReady, setMapReady] = useState(false);
  const [mapZoom, setMapZoom] = useState(DEFAULT_ZOOM);
  const [liveLinks, setLiveLinks] = useState<Link[]>([]);
  const [liveHubs, setLiveHubs] = useState<Hub[]>([]);
  const linkProjectionCacheRef = useRef<SnapshotLinkProjectionCache>(new Map());
  const [packetFlights, setPacketFlights] = useState<{
    packets: Packet[];
    loopPackets: LoopPacket[];
    preferLoopNext?: boolean;
  }>({ packets: [], loopPackets: [], preferLoopNext: false });
  const packets = publicMode ? [] : packetFlights.packets;
  const loopPackets = publicMode ? [] : packetFlights.loopPackets;
  const [nowSec, setNowSec] = useState(0);
  const [trafficById, setTrafficById] = useState<Record<string, LinkTraffic>>({});
  const [trafficEnabled, setTrafficEnabled] = useState(false);
  const [trafficResponseCurrent, setTrafficResponseCurrent] = useState(false);
  const [trafficReadySignature, setTrafficReadySignature] = useState('');
  const [trafficReady, setTrafficReady] = useState(false);
  const [trafficUpdatedAtMs, setTrafficUpdatedAtMs] = useState<number | undefined>(undefined);
  const [showHealthy, setShowHealthy] = useState(initialShowHealthy);
  const [showBroken, setShowBroken] = useState(initialShowBroken);
  const [selectedLink, setSelectedLink] = useState<Link | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerSheet, setDrawerSheet] = useState<'graphs' | 'details'>('graphs');
  const [seriesData, setSeriesData] = useState<TrafficSeriesResponse | null>(null);
  const [seriesLoading, setSeriesLoading] = useState(false);
  const [seriesError, setSeriesError] = useState<string | null>(null);
  const [seriesDisabled, setSeriesDisabled] = useState(false);
  const [seriesRetryToken, setSeriesRetryToken] = useState(0);
  const [seriesUpdatedAtMs, setSeriesUpdatedAtMs] = useState<number | undefined>(undefined);
  const [seriesRangeSeconds, setSeriesRangeSeconds] = useState(TRAFFIC_SERIES_RANGE_SECONDS);
  const [packetRateScale, setPacketRateScale] = useState(PACKET_RATE_SCALE_DEFAULT);
  const [seriesRealtime, setSeriesRealtime] = useState(false);
  const [seriesNowMs, setSeriesNowMs] = useState<number | undefined>(undefined);

  const [isCompact, setIsCompact] = useState(false);
  const [hoverInfo, setHoverInfo] = useState<{ x: number; y: number; link: Link } | null>(null);
  const [hoverHubDevice, setHoverHubDevice] = useState<{
    x: number;
    y: number;
    datum: HubDeviceMarkerDatum;
  } | null>(null);
  const [hoverPosition, setHoverPosition] = useState<{ x: number; y: number } | null>(null);
  const [pixelRatio, setPixelRatio] = useState(() => window.devicePixelRatio ?? 1);
  const [flashLinkId, setFlashLinkId] = useState<string | null>(null);
  const [brokenActiveId, setBrokenActiveId] = useState<string | null>(null);
  const [brokenPanelExpanded, setBrokenPanelExpanded] = useState(false);
  const [debugAutoPanInfo, setDebugAutoPanInfo] = useState<string>('');
  const [autoPanEnabled, setAutoPanEnabled] = useState(false);
  const seriesPanelOpen = drawerOpen;
  const selectedSeriesLinkID = selectedLink?.id ?? '';
  const selectedSeriesIdentity = buildTrafficSeriesIdentity(snapshot?.tenant_id, selectedLink);
  const linkDetailViewModel = useMemo(
    () =>
      buildLinkDetailTelemetryViewModel(seriesData, {
        loading: seriesLoading,
        nowMs: seriesNowMs,
        error: seriesError,
        disabled: seriesDisabled
      }),
    [seriesData, seriesDisabled, seriesError, seriesLoading, seriesNowMs]
  );
  const hubDeviceData = useMemo(
    () => projectHubDeviceMarkers(
      snapshot?.hubs ?? [],
      snapshot?.devices ?? [],
      snapshot?.active_alerts ?? []
    ),
    [snapshot?.active_alerts, snapshot?.devices, snapshot?.hubs]
  );
  const hubDeviceDrawer = useHubDeviceDrawerController({
    tenantID: snapshot?.tenant_id,
    data: hubDeviceData
  });
  const deviceHealthPalette = useMemo(() => readDeviceHealthDeckPalette(), []);

  const mapRef = useRef<MapLibreMap | null>(null);
  const autoPanEnabledRef = useRef(autoPanEnabled);
  const hoverRef = useRef<HTMLDivElement | null>(null);
  const flashTimeoutRef = useRef<number | null>(null);
  const lastAutoPanRef = useRef<number>(0);
  const lastBrokenSignatureRef = useRef<string>('');
  const brokenSignatureChangedAtRef = useRef<number>(0);
  const brokenAutoIndexRef = useRef(0);
  const nextAutoPanAtRef = useRef(0);
  const lastFocusedBrokenIdRef = useRef<string | null>(null);
  const autoRotateTokenRef = useRef(0);
  const autoRotateActiveRef = useRef(false);
  const divRef = useRef<HTMLDivElement | null>(null);
  const drawerInvokerRef = useRef<HTMLElement | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const packetLastTickRef = useRef<number>(0);
  const lastEmitRef = useRef<number>(0);
  const lastPruneRef = useRef<number>(0);
  const packetIdRef = useRef(0);
  const lastServerTimeRef = useRef<string | null>(null);
  const lastTrafficQueryRef = useRef<number>(0);
  const lastTrafficSignatureRef = useRef<string>('');
  const trafficLinksRef = useRef<TrafficLinkRequest[]>([]);
  const hasFitBoundsRef = useRef(false);
  const packetWeightsForwardRef = useRef<{ entries: Array<{ link: Link; cumulative: number }>; total: number }>({
    entries: [],
    total: 0
  });
  const packetWeightsBackwardRef = useRef<{ entries: Array<{ link: Link; cumulative: number }>; total: number }>({
    entries: [],
    total: 0
  });
  const packetWeightsWarningForwardRef = useRef<{ entries: Array<{ link: Link; cumulative: number }>; total: number }>({
    entries: [],
    total: 0
  });
  const packetWeightsWarningBackwardRef = useRef<{ entries: Array<{ link: Link; cumulative: number }>; total: number }>({
    entries: [],
    total: 0
  });
  const packetWeightsBrokenRef = useRef<{ entries: Array<{ link: Link; cumulative: number }>; total: number }>({
    entries: [],
    total: 0
  });
  const loopPacketWeightsRef = useRef<{ entries: Array<{ ring: LoopRing; cumulative: number }>; total: number }>({
    entries: [],
    total: 0
  });
  const baseLinks = useMemo(() => liveLinks, [liveLinks]);
  const networkStyleURL = publicMode ? '/api/v1/public/status/map-style' : NETWORK_STYLE_URL;
  const basemapURL = publicMode ? '/api/v1/public/status/basemap/pmtiles' : BASEMAP_PMTILES_URL;
  const mapStyle = useMemo(
    () => buildBaseStyle(baseStyle, basemapAvailable, basemapURL),
    [baseStyle, basemapAvailable, basemapURL]
  );
  useEffect(() => {
    autoPanEnabledRef.current = autoPanEnabled;
  }, [autoPanEnabled]);
  useEffect(() => {
    if (!autoPanEnabled) {
      lastFocusedBrokenIdRef.current = null;
    }
  }, [autoPanEnabled]);
  const clearAutoRotate = useCallback(
    (stopMap: boolean) => {
      autoRotateActiveRef.current = false;
      autoRotateTokenRef.current += 1;
      if (stopMap) {
        const map = mapRef.current;
        if (map) {
          map.stop();
        }
      }
    },
    []
  );

  const stopAutoRotate = useCallback(() => {
    clearAutoRotate(true);
  }, [clearAutoRotate]);

  const haltAutoRotate = useCallback(() => {
    clearAutoRotate(false);
  }, [clearAutoRotate]);

  const enableMapInteractions = useCallback(() => {
    const map = mapRef.current;
    if (!map) return;
    map.dragPan?.enable();
    map.scrollZoom?.enable();
    map.boxZoom?.enable();
    map.doubleClickZoom?.enable();
    map.keyboard?.enable();
    map.touchZoomRotate?.enable();
    map.dragRotate?.enable();
    map.touchPitch?.enable();
  }, []);

  const scheduleAutoRotate = useCallback(() => {
    const map = mapRef.current;
    if (!map) return;
    autoRotateActiveRef.current = true;
    const token = ++autoRotateTokenRef.current;
    const spin = () => {
      if (!autoRotateActiveRef.current || autoRotateTokenRef.current !== token) return;
      map.easeTo({
        bearing: map.getBearing() + AUTO_PAN_ROTATE_STEP_DEG,
        duration: AUTO_PAN_ROTATE_STEP_MS,
        easing: (t: number) => t
      });
      map.once('moveend', () => {
        if (!autoRotateActiveRef.current || autoRotateTokenRef.current !== token) return;
        spin();
      });
    };
    if (map.isMoving()) {
      map.once('moveend', () => {
        if (!autoRotateActiveRef.current || autoRotateTokenRef.current !== token) return;
        spin();
      });
      return;
    }
    spin();
  }, []);
  const trafficLinks = useMemo(() => {
    const candidates = baseLinks
      .filter(link => !isUnconfirmedTopologyLink(link))
      .map(link => ({
        id: link.id,
        source_host: link.telemetry_host_a,
        source_ifindex: link.telemetry_ifindex_a,
        target_host: link.telemetry_host_b,
        target_ifindex: link.telemetry_ifindex_b
      }))
      .filter(link => Boolean(link.source_host && link.source_ifindex) || Boolean(link.target_host && link.target_ifindex));
    candidates.sort((a, b) => a.id.localeCompare(b.id));
    return candidates;
  }, [baseLinks]);

  const trafficSignature = useMemo(() => {
    if (!trafficLinks.length) return '';
    return trafficLinks
      .map(link => `${link.id}:${link.source_host ?? ''}:${link.source_ifindex ?? ''}:${link.target_host ?? ''}:${link.target_ifindex ?? ''}`)
      .join('|');
  }, [trafficLinks]);
  useEffect(() => {
    trafficLinksRef.current = trafficLinks;
  }, [trafficLinks]);
  useEffect(() => {
    setTrafficReady(false);
  }, [trafficSignature]);
  // Canonical link health comes from svc-status. Traffic enriches graphs and
  // animation only; the browser must never reinterpret operational health.
  const links = baseLinks;
  const confirmedLinks = useMemo(() => links.filter(link => !isUnconfirmedTopologyLink(link)), [links]);
  const packetsEstimated =
    telemetryEstimated ||
    !trafficEnabled ||
    !trafficResponseCurrent ||
    confirmedLinks.some(link => !trafficById[link.id]);
  const showEndpointDebug = staticMode || DEBUG_ENDPOINTS;
  const brokenLinks = useMemo(() => confirmedLinks.filter(link => isProblemStatus(link.status)), [confirmedLinks]);
  const hasBrokenLinks = brokenLinks.length > 0;
  const brokenLinksSorted = useMemo(() => {
    return [...brokenLinks].sort((a, b) => {
      const aTime = a.updated_at_ms ?? 0;
      const bTime = b.updated_at_ms ?? 0;
      return bTime - aTime;
    });
  }, [brokenLinks]);
  useEffect(() => {
    const signature = brokenLinksSorted.map(link => link.id).join('|');
    if (signature !== lastBrokenSignatureRef.current) {
      lastBrokenSignatureRef.current = signature;
      brokenSignatureChangedAtRef.current = Date.now();
    }
  }, [brokenLinksSorted]);
  const brokenActiveIndex = useMemo(() => {
    if (!brokenLinksSorted.length) return 0;
    if (!brokenActiveId) return 0;
    const idx = brokenLinksSorted.findIndex(link => link.id === brokenActiveId);
    return idx >= 0 ? idx : 0;
  }, [brokenActiveId, brokenLinksSorted]);
  const brokenActiveLink = brokenLinksSorted[brokenActiveIndex];
  const linkCounts = useMemo(() => {
    let warning = 0;
    let broken = 0;
    let healthy = 0;
    confirmedLinks.forEach(link => {
      if (link.status === LINK_STATUS_WARNING) {
        warning += 1;
      } else if (isProblemStatus(link.status)) {
        broken += 1;
      } else {
        healthy += 1;
      }
    });
    return { warning, broken, healthy, issues: warning + broken, total: confirmedLinks.length };
  }, [confirmedLinks]);
  const mapLinks = useMemo(() => suppressLagMemberArcs(links), [links]);
  const coHubMap = useMemo(() => {
    const hubs = new Map<string, CoHubEntry>();
    liveHubs.forEach(hub => {
      if (!hub.location) return;
      const entry: CoHubEntry = { id: hub.id, center: hub.location, links: [], hub };
      hubLookupKeys(hub).forEach(key => hubs.set(key, entry));
    });
    const addHub = (id: number | undefined, lat?: number, lng?: number, link?: Link) => {
      if (!id) return;
      const coord = resolveCoCoord(lat, lng);
      if (!coord) return;
      const key = String(id);
      const existing = hubs.get(key);
      if (existing) {
        if (link) existing.links.push(link);
        return;
      }
      hubs.set(key, { id: key, center: coord, links: link ? [link] : [] });
    };
    mapLinks.forEach(link => {
      addHub(link.source_co_id, link.source_co_lat, link.source_co_lng, link);
      addHub(link.target_co_id, link.target_co_lat, link.target_co_lng, link);
    });
    return hubs;
  }, [liveHubs, mapLinks]);

  const activeLinkCoordKeys = useMemo(() => {
    const keys = new Set<string>();
    mapLinks.forEach(link => {
      keys.add(coordKey(link.source));
      keys.add(coordKey(link.target));
      if (link.renderSource) keys.add(coordKey(link.renderSource));
      if (link.renderTarget) keys.add(coordKey(link.renderTarget));
    });
    return keys;
  }, [mapLinks]);

  const mapHubs = useMemo(() => {
    const seen = new Set<string>();
    return Array.from(coHubMap.values()).filter(entry => {
      const hasActiveLink = entry.links.length > 0 || activeLinkCoordKeys.has(coordKey(entry.center));
      if (!entry.hub || !hasActiveLink || seen.has(entry.hub.id)) return false;
      seen.add(entry.hub.id);
      return true;
    });
  }, [activeLinkCoordKeys, coHubMap]);

  const { coClusters, regularLinks } = useMemo(() => {
    const clusters = new Map<string, CoCluster>();
    const regular: Link[] = [];
    mapLinks.forEach(link => {
      const co = resolveCoCenter(link, coHubMap);
      if (co) {
        const entry = clusters.get(co.id);
        if (entry) {
          entry.links.push(link);
        } else {
          clusters.set(co.id, {
            id: `co-${co.id}`,
            center: co.center,
            links: [link]
          });
        }
        return;
      }

      const distance = haversineDistanceMeters(link.source, link.target);
      if (distance < CO_DISTANCE_METERS) {
        const fallbackId = `fallback-${coordKey(link.source)}`;
        const entry = clusters.get(fallbackId);
        if (entry) {
          entry.links.push(link);
        } else {
          clusters.set(fallbackId, {
            id: fallbackId,
            center: link.source,
            links: [link]
          });
        }
        return;
      }

      regular.push(link);
    });

    const clusterList = Array.from(clusters.values());
    clusterList.sort((a, b) => compareCoords(a.center, b.center));
    return { coClusters: clusterList, regularLinks: regular };
  }, [coHubMap, mapLinks]);
  const displayLinks = useMemo(() => {
    const grouped = new Map<string, Link[]>();
    regularLinks.forEach(link => {
      const sourceHub = link.source_co_id ? coHubMap.get(String(link.source_co_id)) : undefined;
      const targetHub = link.target_co_id ? coHubMap.get(String(link.target_co_id)) : undefined;
      const renderSource = sourceHub?.center ?? resolveCoCoord(link.source_co_lat, link.source_co_lng) ?? link.source;
      const renderTarget = targetHub?.center ?? resolveCoCoord(link.target_co_lat, link.target_co_lng) ?? link.target;
      const key = pairKey(renderSource, renderTarget);
      const list = grouped.get(key);
      if (list) {
        list.push({ ...link, renderSource, renderTarget });
      } else {
        grouped.set(key, [{ ...link, renderSource, renderTarget }]);
      }
    });
    const adjusted: Link[] = [];
    grouped.forEach(group => {
      const sorted = [...group].sort((a, b) => a.id.localeCompare(b.id));
      sorted.forEach((link, index) => {
        const laneOffset = index - (sorted.length - 1) / 2;
        const height = Math.max(
          ARC_HEIGHT_MIN,
          Math.min(ARC_HEIGHT_MAX, ARC_HEIGHT + laneOffset * ARC_LANE_HEIGHT_STEP)
        );
        adjusted.push({ ...link, renderHeight: height });
      });
    });
    return adjusted;
  }, [coHubMap, regularLinks]);

  useEffect(() => {
    setShowHealthy(initialShowHealthy);
  }, [initialShowHealthy]);

  useEffect(() => {
    setShowBroken(initialShowBroken);
  }, [initialShowBroken]);

  useEffect(() => {
    if (staticMode) {
      setLiveLinks(buildDemoLinks());
      setLiveHubs([]);
      lastServerTimeRef.current = new Date().toISOString();
      setTrafficEnabled(false);
      return;
    }
    if (DEMO_NETWORK) {
      setLiveLinks(buildDemoLinks());
      setLiveHubs([]);
      lastServerTimeRef.current = new Date().toISOString();
      setTrafficEnabled(false);
      return;
    }
    if (DEBUG_ONLY) {
      setLiveLinks([]);
      setLiveHubs([]);
      lastServerTimeRef.current = new Date().toISOString();
      setTrafficEnabled(false);
    }
  }, [staticMode]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(networkStyleURL, { cache: 'no-store' });
        if (!res.ok) throw new Error(`style: ${res.status}`);
        const json = (await res.json()) as Record<string, any>;
        if (!cancelled) {
          setBaseStyle(json);
          setStyleError(null);
        }
      } catch (err) {
        if (!cancelled) setStyleError((err as Error).message ?? 'style: unavailable');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [networkStyleURL]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const basemapOk = await checkPmtilesHeader(basemapURL);
      if (!cancelled) {
        setBasemapAvailable(basemapOk);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [basemapURL]);

  const haltAutoRotateIfFree = useCallback(() => {
    if (autoPanEnabledRef.current) {
      return;
    }
    haltAutoRotate();
  }, [haltAutoRotate]);

  useEffect(() => {
    if (!divRef.current) return;

    if (mapRef.current) {
      mapRef.current.setStyle(mapStyle);
      return;
    }

    const map = new maplibregl.Map({
      container: divRef.current,
      style: mapStyle,
      center: DEFAULT_CENTER,
      zoom: DEFAULT_ZOOM,
      pitch: DEFAULT_PITCH,
      attributionControl: false
    });

    const handleResize = () => {
      setPixelRatio(map.getPixelRatio());
    };
    const handleZoom = () => {
      setMapZoom(map.getZoom());
    };
    map.once('load', () => {
      setMapReady(true);
      setPixelRatio(map.getPixelRatio());
      handleZoom();
    });
    map.on('resize', handleResize);
    map.on('zoom', handleZoom);
    map.on('dragstart', haltAutoRotateIfFree);
    map.on('touchstart', haltAutoRotateIfFree);
    map.on('mousedown', haltAutoRotateIfFree);
    map.on('wheel', haltAutoRotateIfFree);

    mapRef.current = map;

    return () => {
      map.off('resize', handleResize);
      map.off('zoom', handleZoom);
      map.off('dragstart', haltAutoRotateIfFree);
      map.off('touchstart', haltAutoRotateIfFree);
      map.off('mousedown', haltAutoRotateIfFree);
      map.off('wheel', haltAutoRotateIfFree);
      map.remove();
      mapRef.current = null;
      setMapReady(false);
    };
  }, [haltAutoRotateIfFree, mapStyle]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;
    if (autoPanEnabled) {
      map.stop();
      map.dragPan?.disable();
      map.scrollZoom?.disable();
      map.boxZoom?.disable();
      map.doubleClickZoom?.disable();
      map.keyboard?.disable();
      map.touchZoomRotate?.disable();
      map.dragRotate?.disable();
      map.touchPitch?.disable();
      return;
    }
    stopAutoRotate();
    enableMapInteractions();
  }, [autoPanEnabled, enableMapInteractions, mapReady, stopAutoRotate]);

  useEffect(() => {
    const map = mapRef.current;
    const container = divRef.current;
    if (!map || !container || !mapReady || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => {
      map.resize();
    });
    observer.observe(container);
    return () => {
      observer.disconnect();
    };
  }, [mapReady]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || hasFitBoundsRef.current || !liveLinks.length) return;
    const points = liveLinks.flatMap(link => [link.source, link.target]);
    if (!points.length) return;
    let minLng = points[0][0];
    let maxLng = points[0][0];
    let minLat = points[0][1];
    let maxLat = points[0][1];
    points.forEach(([lng, lat]) => {
      if (!Number.isFinite(lng) || !Number.isFinite(lat)) return;
      minLng = Math.min(minLng, lng);
      maxLng = Math.max(maxLng, lng);
      minLat = Math.min(minLat, lat);
      maxLat = Math.max(maxLat, lat);
    });
    if (![minLng, maxLng, minLat, maxLat].every(Number.isFinite)) return;
    map.fitBounds(
      [
        [minLng, minLat],
        [maxLng, maxLat]
      ],
      { padding: 80, duration: 0, maxZoom: 8 }
    );
    map.easeTo({ pitch: DEFAULT_PITCH, duration: 0 });
    hasFitBoundsRef.current = true;
  }, [liveLinks, mapReady]);

  useEffect(() => {
    if (!AUTO_CAMERA_ENABLED) return;
    if (staticMode || DEBUG_ONLY || DEMO_NETWORK || !showBroken) return;
    if (!mapReady || !brokenLinks.length) return;
    const map = mapRef.current;
    if (!map) return;

    const signature = brokenLinks.map(link => link.id).sort().join('|');
    let minLng = Infinity;
    let maxLng = -Infinity;
    let minLat = Infinity;
    let maxLat = -Infinity;
    brokenLinks.forEach(link => {
      [link.source, link.target].forEach(([lng, lat]) => {
        if (!Number.isFinite(lng) || !Number.isFinite(lat)) return;
        minLng = Math.min(minLng, lng);
        maxLng = Math.max(maxLng, lng);
        minLat = Math.min(minLat, lat);
        maxLat = Math.max(maxLat, lat);
      });
    });
    if (![minLng, maxLng, minLat, maxLat].every(Number.isFinite)) return;

    let timeoutId: number | null = null;
    const attemptPan = () => {
      const now = Date.now();
      if (signature === lastBrokenSignatureRef.current && now-lastAutoPanRef.current < AUTO_CAMERA_MIN_INTERVAL_MS) {
        return;
      }
      const padding = isCompact ? 48 : 96;
      map.fitBounds(
        [
          [minLng, minLat],
          [maxLng, maxLat]
        ],
        { padding, duration: 1200, maxZoom: 8 }
      );
      lastAutoPanRef.current = now;
      lastBrokenSignatureRef.current = signature;
    };

    attemptPan();

    return () => {
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [brokenLinks, isCompact, mapReady, showBroken, staticMode]);

  useEffect(() => {
    if (staticMode || DEMO_NETWORK || DEBUG_ONLY || !snapshot) return;
    lastServerTimeRef.current = snapshot.server_time;
  }, [snapshot?.server_time, staticMode]);

  useEffect(() => {
    if (staticMode || DEMO_NETWORK || DEBUG_ONLY || !snapshot) return;
    const reconciled = reconcileSnapshotLinkProjection(
      snapshot.links,
      linkProjectionCacheRef.current
    );
    linkProjectionCacheRef.current = reconciled.cache;
    setLiveLinks(reconciled.links);
  }, [snapshot?.links, staticMode]);

  useEffect(() => {
    if (staticMode || DEMO_NETWORK || DEBUG_ONLY || !snapshot) return;
    setLiveHubs(snapshot.hubs.map(normalizeHub).filter(Boolean) as Hub[]);
  }, [snapshot?.hubs, staticMode]);

  const getDirectionalPps = useCallback(
    (link: Link, direction: PacketDirection) => {
      const capacityFallback = () => {
        const mbps = Math.max(1, Number(link.bandwidth_mbps) || 1);
        return (mbps * 1_000_000) / (8 * 1500);
      };
      if (!trafficEnabled) return capacityFallback();
      const traffic = trafficById[link.id];
      if (!traffic) return capacityFallback();
      const pps = direction === 'forward' ? traffic.source_pps : traffic.target_pps;
      const resolved = Number.isFinite(pps) ? pps : 0;
      const bounded = Math.min(Math.max(resolved, MIN_PPS_PER_LINK), MAX_PPS_PER_LINK);
      return link.status !== 'healthy' ? Math.min(resolved, MAX_PPS_PER_LINK) : bounded;
    },
    [trafficById, trafficEnabled]
  );

  const getLinkUtilization = useCallback(
    (link: Link): number | null => {
      const resolveDirectUtilization = (candidate: Link): number | null => {
        const fallback = getStateUtilization(candidate);
        if (!trafficEnabled) return fallback;
        const traffic = trafficById[candidate.id];
        if (!traffic) return fallback;
        const capacityMbps = getDirectCapacityMbps(candidate);
        if (!capacityMbps) return fallback;
        const capacityBps = capacityMbps * 1_000_000;
        if (!Number.isFinite(capacityBps) || capacityBps <= 0) return fallback;
        const source = Number(traffic.source_bps) || 0;
        const target = Number(traffic.target_bps) || 0;
        const util = Math.max(source, target) / capacityBps;
        if (!Number.isFinite(util)) return fallback;
        return Math.max(0, util);
      };

      const direct = resolveDirectUtilization(link);
      if (direct !== null) return direct;

      const memberStats = getLagMemberRuntimeLinks(link, links)
        .map(member => ({
          capacity: getDirectCapacityMbps(member),
          utilization: resolveDirectUtilization(member)
        }))
        .filter(stat => stat.utilization !== null);
      if (!memberStats.length) return getDeclaredLagMemberUtilization(link);

      const weighted = memberStats.reduce(
        (acc, stat) => {
          if (stat.capacity !== null && stat.capacity > 0 && stat.utilization !== null) {
            acc.capacity += stat.capacity;
            acc.used += stat.capacity * stat.utilization;
          }
          return acc;
        },
        { capacity: 0, used: 0 }
      );
      if (weighted.capacity > 0) {
        return Math.max(0, weighted.used / weighted.capacity);
      }
      return Math.max(...memberStats.map(stat => stat.utilization as number));
    },
    [links, trafficById, trafficEnabled]
  );

  const getLinkOpticBrightness = useCallback(
    (link: Link): number | null => {
      if (!trafficEnabled) return null;
      const traffic = trafficById[link.id];
      if (!traffic) return null;
      const lightValues = [traffic.source_light_dbm, traffic.target_light_dbm]
        .map(value => Number(value))
        .filter(value => Number.isFinite(value));
      if (!lightValues.length) return null;
      const strongestDbm = Math.max(...lightValues);
      const normalized = clamp((strongestDbm + 20) / 23, 0, 1);
      return 0.65 + normalized * 0.6;
    },
    [trafficById, trafficEnabled]
  );

  const getBrokenPps = useCallback(
    (link: Link) => {
      if (!trafficEnabled) {
        const mbps = Math.max(1, Number(link.bandwidth_mbps) || 1);
        return (mbps * 1_000_000) / (8 * 1500);
      }
      const traffic = trafficById[link.id];
      if (!traffic) return 0;
      const source = Number(traffic.source_pps) || 0;
      const target = Number(traffic.target_pps) || 0;
      return Math.min(Math.max(0, (source + target) / 2), MAX_PPS_PER_LINK);
    },
    [trafficById, trafficEnabled]
  );

  const getLoopPps = useCallback(
    (link: Link) => {
      if (!trafficEnabled) {
        const mbps = Math.max(1, Number(link.bandwidth_mbps) || 1);
        return (mbps * 1_000_000) / (8 * 1500);
      }
      const traffic = trafficById[link.id];
      if (!traffic) return 0;
      const source = Number(traffic.source_pps) || 0;
      const target = Number(traffic.target_pps) || 0;
      const avg = (source + target) / 2;
      if (link.status !== 'healthy') {
        return Math.min(Math.max(0, avg), MAX_PPS_PER_LINK);
      }
      return Math.min(Math.max(avg, MIN_PPS_PER_LINK), MAX_PPS_PER_LINK);
    },
    [trafficById, trafficEnabled]
  );

  const handleHover = useCallback((info: PickingInfo) => {
    const map = mapRef.current;
    const canvas = map?.getCanvas?.();
    const hubDevice =
      (info?.object as HubDeviceMarkerDatum | undefined)?.kind === 'hub-device-health'
        ? (info.object as HubDeviceMarkerDatum)
        : null;
    if (canvas) {
      const maybe = info?.object as { link?: Link } | Link | undefined;
      const link = maybe && 'link' in maybe ? maybe.link : (maybe as Link | undefined);
      canvas.style.cursor = link?.id || hubDevice ? 'pointer' : '';
    }
    if (autoPanEnabled) {
      setHoverInfo(null);
      setHoverHubDevice(null);
      return;
    }
    if (hubDevice) {
      setHoverInfo(null);
      setHoverHubDevice({ x: info.x ?? 0, y: info.y ?? 0, datum: hubDevice });
      return;
    }
    setHoverHubDevice(null);
    if (!info?.object) {
      setHoverInfo(null);
      return;
    }
    const maybe = info.object as { link?: Link } | Link;
    const link = 'link' in maybe ? maybe.link : (maybe as Link);
    if (!link?.id) {
      setHoverInfo(null);
      return;
    }
    setHoverInfo({ x: info.x ?? 0, y: info.y ?? 0, link });
  }, [autoPanEnabled]);

  const triggerFlash = useCallback((linkId: string) => {
    setFlashLinkId(linkId);
    if (flashTimeoutRef.current) {
      window.clearTimeout(flashTimeoutRef.current);
    }
    flashTimeoutRef.current = window.setTimeout(() => {
      setFlashLinkId(current => (current === linkId ? null : current));
      flashTimeoutRef.current = null;
    }, 2400);
  }, []);

  const openSeriesDrawer = useCallback((link: Link) => {
    if (publicMode) return;
    hubDeviceDrawer.close();
    drawerInvokerRef.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setSelectedLink(link);
    setSeriesRangeSeconds(TRAFFIC_SERIES_RANGE_SECONDS);
    setSeriesRealtime(false);
    setSeriesData(null);
    setSeriesError(null);
    setSeriesUpdatedAtMs(undefined);
    setSeriesNowMs(undefined);
    setDrawerSheet('graphs');
    setDrawerOpen(true);
  }, [hubDeviceDrawer.close, publicMode]);

  const closeSeriesDrawer = useCallback(() => {
    const invoker = drawerInvokerRef.current;
    drawerInvokerRef.current = null;
    setDrawerOpen(false);
    setSelectedLink(null);
    setDrawerSheet('graphs');
    setSeriesData(null);
    setSeriesError(null);
    setSeriesUpdatedAtMs(undefined);
    setSeriesNowMs(undefined);
    window.requestAnimationFrame(() => invoker?.focus());
  }, []);

  useEffect(() => {
    if (!drawerOpen) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeSeriesDrawer();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [drawerOpen, closeSeriesDrawer]);

  const handleClick = useCallback((info: PickingInfo) => {
    if (publicMode) return;
    if (!info?.object) {
      return;
    }
    const hubDevice = info.object as HubDeviceMarkerDatum;
    if (hubDevice.kind === 'hub-device-health') {
      closeSeriesDrawer();
      hubDeviceDrawer.openHub(
        hubDevice,
        document.activeElement instanceof HTMLElement ? document.activeElement : null
      );
      return;
    }
    const maybe = info.object as { link?: Link } | Link;
    const link = 'link' in maybe ? maybe.link : (maybe as Link);
    if (!link?.id) {
      return;
    }
    openSeriesDrawer(link);
  }, [closeSeriesDrawer, hubDeviceDrawer.openHub, openSeriesDrawer, publicMode]);

  const focusOnLink = useCallback(
    (link: Link, opts?: { fromAuto?: boolean }) => {
      const map = mapRef.current;
      if (!map) return;
      const sourceHub = link.source_co_id ? coHubMap.get(String(link.source_co_id)) : undefined;
      const targetHub = link.target_co_id ? coHubMap.get(String(link.target_co_id)) : undefined;
      const renderSource =
        sourceHub?.center ?? resolveCoCoord(link.source_co_lat, link.source_co_lng) ?? link.source;
      const renderTarget =
        targetHub?.center ?? resolveCoCoord(link.target_co_lat, link.target_co_lng) ?? link.target;
      const points = [renderSource, renderTarget].filter(
        coord => Number.isFinite(coord?.[0]) && Number.isFinite(coord?.[1])
      ) as Array<[number, number]>;
      if (!points.length) return;
      const distanceMeters = points.length > 1 ? haversineDistanceMeters(points[0], points[1]) : 0;
      const shouldApplyAutoRotate = (targetCenter: [number, number], targetZoom?: number) => {
        if (!opts?.fromAuto) return false;
        const currentCenter = map.getCenter();
        const centerDistance = haversineDistanceMeters(
          [currentCenter.lng, currentCenter.lat],
          targetCenter
        );
        if (centerDistance < 5) {
          if (typeof targetZoom === 'number') {
            return Math.abs(map.getZoom() - targetZoom) >= 0.05;
          }
          return false;
        }
        return true;
      };
      if (distanceMeters < CO_DISTANCE_METERS) {
        const co = resolveCoCenter(link, coHubMap);
        const center = co?.center ?? points[0];
        if (!opts?.fromAuto) {
          haltAutoRotate();
        }
        const autoPan = Boolean(opts?.fromAuto);
        if (autoPan) {
          map.stop();
          stopAutoRotate();
        }
        if (DEBUG_AUTOPAN) {
          const info = {
            id: link.id,
            status: link.status,
            source: link.source,
            target: link.target,
            renderSource,
            renderTarget,
            co: co?.id ?? null,
            distanceMeters
          };
          console.info('auto-pan focus (co)', info);
          setDebugAutoPanInfo(`focus ${link.id} (${link.status}) co=${co?.id ?? '—'}`);
        }
        const targetZoom = Math.min(map.getMaxZoom(), 15);
        const applyRotate = shouldApplyAutoRotate(center, targetZoom);
        map.easeTo({
          center,
          zoom: targetZoom,
          duration: autoPan ? AUTO_PAN_DURATION_MS : 800,
          pitch: autoPan ? AUTO_PAN_PITCH : map.getPitch(),
          bearing: autoPan && applyRotate ? map.getBearing() + AUTO_PAN_ROTATE_DEGREES : map.getBearing()
        });
        if (autoPan) {
          scheduleAutoRotate();
        } else {
          enableMapInteractions();
        }
        triggerFlash(link.id);
        return;
      }
      const maxZoom = getFocusMaxZoom(distanceMeters);
      const padding = getFocusPadding(distanceMeters, isCompact);
      let minLng = points[0][0];
      let maxLng = points[0][0];
      let minLat = points[0][1];
      let maxLat = points[0][1];
      points.forEach(([lng, lat]) => {
        minLng = Math.min(minLng, lng);
        maxLng = Math.max(maxLng, lng);
        minLat = Math.min(minLat, lat);
        maxLat = Math.max(maxLat, lat);
      });
      const autoPan = Boolean(opts?.fromAuto);
      if (!autoPan) {
        haltAutoRotate();
      }
      if (autoPan) {
        map.stop();
        stopAutoRotate();
      }
      if (DEBUG_AUTOPAN) {
        const info = {
          id: link.id,
          status: link.status,
          source: link.source,
          target: link.target,
          renderSource,
          renderTarget,
          distanceMeters
        };
        console.info('auto-pan focus', info);
        setDebugAutoPanInfo(`focus ${link.id} (${link.status}) dist=${Math.round(distanceMeters)}m`);
      }
      const boundsCenter: [number, number] = [(minLng + maxLng) / 2, (minLat + maxLat) / 2];
      const applyRotate = shouldApplyAutoRotate(boundsCenter);
      map.fitBounds(
        [
          [minLng, minLat],
          [maxLng, maxLat]
        ],
        {
          padding,
          duration: autoPan ? AUTO_PAN_DURATION_MS : 800,
          maxZoom,
          pitch: autoPan ? AUTO_PAN_PITCH : map.getPitch(),
          bearing: autoPan && applyRotate ? map.getBearing() + AUTO_PAN_ROTATE_DEGREES : map.getBearing(),
          easing: autoPan ? (t: number) => t : undefined
        }
      );
      if (autoPan) {
        scheduleAutoRotate();
      } else {
        enableMapInteractions();
      }
      triggerFlash(link.id);
    },
    [coHubMap, haltAutoRotate, isCompact, scheduleAutoRotate, stopAutoRotate, triggerFlash]
  );

  useEffect(() => {
    if (!focusedAlert || !mapReady) return;
    const resourceID = focusedAlert.resource_id;
    const link = focusedAlert.resource_kind === 'link' && resourceID
      ? links.find(candidate => candidate.id === resourceID)
      : focusedAlert.resource_kind === 'device' && resourceID
        ? links.find(
            candidate =>
              candidate.source_device_id === resourceID ||
              candidate.target_device_id === resourceID
          )
        : focusedAlert.resource_kind === 'interface' && resourceID
          ? links.find(
              candidate =>
                candidate.source_interface_id === resourceID ||
                candidate.target_interface_id === resourceID
            )
        : undefined;
    if (!link) return;
    setBrokenActiveId(link.id);
    focusOnLink(link);
  }, [focusOnLink, focusedAlert, links, mapReady]);

  const navigateBrokenLink = useCallback(
    (direction: 'prev' | 'next') => {
      if (!brokenLinksSorted.length) return;
      const length = brokenLinksSorted.length;
      const currentIndex = brokenActiveId
        ? brokenLinksSorted.findIndex(link => link.id === brokenActiveId)
        : -1;
      let nextIndex = currentIndex >= 0 ? currentIndex : 0;
      nextIndex = direction === 'prev' ? (nextIndex - 1 + length) % length : (nextIndex + 1) % length;
      const nextLink = brokenLinksSorted[nextIndex];
      if (!nextLink) return;
      brokenAutoIndexRef.current = nextIndex;
      nextAutoPanAtRef.current = Date.now() + BROKEN_CAROUSEL_INTERVAL_MS;
      setBrokenActiveId(nextLink.id);
      lastFocusedBrokenIdRef.current = nextLink.id;
      focusOnLink(nextLink, { fromAuto: autoPanEnabled });
      lastAutoPanRef.current = Date.now();
    },
    [autoPanEnabled, brokenActiveId, brokenLinksSorted, focusOnLink]
  );

  const handleZoomIn = useCallback(() => {
    mapRef.current?.zoomIn({ duration: 200 });
  }, []);

  const handleZoomOut = useCallback(() => {
    mapRef.current?.zoomOut({ duration: 200 });
  }, []);

  useEffect(() => {
    if (!trafficSignature) {
      setTrafficEnabled(false);
      setTrafficResponseCurrent(false);
      setTrafficById({});
      setTrafficReadySignature('');
      setTrafficReady(false);
      setTrafficUpdatedAtMs(undefined);
      lastTrafficSignatureRef.current = '';
      return;
    }
    let cancelled = false;
    let polling = false;
    let pollPending = false;
    const runPoll = async () => {
      const now = Date.now();
      const signature = trafficSignature;
      const recentlyPolled = now - lastTrafficQueryRef.current < TRAFFIC_QUERY_MIN_INTERVAL_MS;
      const signatureUnchanged = signature === lastTrafficSignatureRef.current;
      if (recentlyPolled && signatureUnchanged) return;
      if (signatureUnchanged && now - lastTrafficQueryRef.current < TRAFFIC_POLL_INTERVAL_MS) return;
      lastTrafficQueryRef.current = now;
      lastTrafficSignatureRef.current = signature;
      try {
        const linksForRequest = trafficLinksRef.current;
        if (!linksForRequest.length) return;
        const res = await fetch(TRAFFIC_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          cache: 'no-store',
          body: JSON.stringify({ links: linksForRequest })
        });
        if (!res.ok) {
          if (!cancelled) setTrafficResponseCurrent(false);
          return;
        }
        const payload = (await res.json()) as TrafficResponse;
        if (cancelled) return;
        const enabled = Boolean(payload?.enabled);
        setTrafficEnabled(enabled);
        if (!enabled) {
          setTrafficResponseCurrent(false);
          setTrafficById({});
          setTrafficReadySignature('');
          setTrafficReady(false);
          setTrafficUpdatedAtMs(undefined);
          return;
        }
        const updatedAtRaw = payload?.telemetry_updated_at;
        const updatedAtMs = updatedAtRaw ? Date.parse(updatedAtRaw) : NaN;
        setTrafficUpdatedAtMs(Number.isFinite(updatedAtMs) ? updatedAtMs : undefined);
        const nextLinks = Array.isArray(payload?.links) ? payload.links : [];
        const nextMap: Record<string, LinkTraffic> = {};
        nextLinks.forEach(link => {
          if (!link?.id) return;
          nextMap[String(link.id)] = link;
        });
        setTrafficResponseCurrent(linksForRequest.every(link => Boolean(nextMap[link.id])));
        const currentLinkIDs = new Set(linksForRequest.map(link => link.id));
        setTrafficById(previous => {
          const merged: Record<string, LinkTraffic> = {};
          currentLinkIDs.forEach(id => {
            if (previous[id]) merged[id] = previous[id];
          });
          Object.assign(merged, nextMap);
          return merged;
        });
        setTrafficReadySignature(signature);
        setTrafficReady(true);
      } catch {
        if (!cancelled) setTrafficResponseCurrent(false);
      }
    };
    const poll = async () => {
      if (polling) {
        pollPending = true;
        return;
      }
      polling = true;
      try {
        do {
          pollPending = false;
          await runPoll();
        } while (pollPending && !cancelled);
      } finally {
        polling = false;
      }
    };

    void poll();
    const interval = window.setInterval(poll, TRAFFIC_POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [trafficSignature]);

  useEffect(() => {
    if (!seriesPanelOpen || !selectedSeriesLinkID) {
      setSeriesData(null);
      setSeriesError(null);
      setSeriesDisabled(false);
      setSeriesLoading(false);
      setSeriesUpdatedAtMs(undefined);
      setSeriesNowMs(undefined);
      return;
    }
    setSeriesData(null);
    setSeriesError(null);
    setSeriesDisabled(false);
    setSeriesLoading(true);
    setSeriesUpdatedAtMs(undefined);

    const poller = createTrafficSeriesPollCoordinator(async signal => {
      setSeriesLoading(true);
      const results: Partial<Record<'interface' | 'sensors', TrafficSeriesResponse>> = {};
      const load = async (group: 'interface' | 'sensors') => {
        try {
          const res = await fetch(TRAFFIC_SERIES_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            cache: 'no-store',
            body: JSON.stringify(buildTrafficSeriesRequest(selectedSeriesLinkID, seriesRangeSeconds, seriesRealtime, group)),
            signal
          });
          const payload = await res.json().catch(() => ({})) as TrafficSeriesResponse & { detail?: string; title?: string };
          if (signal.aborted) return;
          if (!res.ok) throw new Error(safeTrafficSeriesDiagnostic(payload.diagnostic) || safeTrafficSeriesDiagnostic(payload.detail) || safeTrafficSeriesDiagnostic(payload.error) || `Unable to load telemetry graphs (${res.status}).`);
          const state = classifyTrafficSeriesPayload(payload);
          if (state.kind === 'error') throw new Error(state.message);
          results[group] = payload;
          setSeriesData(mergeTrafficSeriesGroups(results.interface, results.sensors));
          if (group === 'interface') {
            setSeriesDisabled(state.kind === 'disabled');
            setSeriesError(null);
            setSeriesLoading(false);
            const parsed = payload.server_time ? Date.parse(payload.server_time) : NaN;
            setSeriesUpdatedAtMs(Number.isFinite(parsed) ? parsed : undefined);
          }
        } catch (err) {
          if (signal.aborted) return;
          if (group === 'interface') {
            setSeriesError((err as Error).message ?? 'Failed to load graphs');
            setSeriesLoading(false);
          } else {
            const groups: LinkTelemetryGroup[] = ['optics', 'temperature'].map(category => ({
              category, availability: 'failed', diagnostic: 'Sensor graphs could not be loaded.'
            }));
            results.sensors = { enabled: true, source: { groups }, target: { groups } };
            setSeriesData(mergeTrafficSeriesGroups(results.interface, results.sensors));
          }
        }
      };
      await Promise.all([load('interface'), load('sensors')]);
      if (!signal.aborted) setSeriesLoading(false);
    });
    poller.poll();
    const interval = window.setInterval(
      poller.poll,
      seriesRealtime ? TRAFFIC_SERIES_REALTIME_POLL_INTERVAL_MS : TRAFFIC_SERIES_DEFAULT_POLL_INTERVAL_MS
    );
    return () => {
      window.clearInterval(interval);
      poller.stop();
    };
  }, [
    seriesPanelOpen,
    selectedSeriesIdentity,
    selectedSeriesLinkID,
    seriesRangeSeconds,
    seriesRealtime,
    seriesRetryToken
  ]);

  useEffect(() => {
    if (!seriesPanelOpen) {
      setSeriesNowMs(undefined);
      return;
    }
    setSeriesNowMs(Date.now());
    const interval = window.setInterval(() => {
      setSeriesNowMs(Date.now());
    }, seriesRealtime ? TRAFFIC_SERIES_REALTIME_TICK_MS : TRAFFIC_SERIES_DEFAULT_TICK_MS);
    return () => {
      window.clearInterval(interval);
    };
  }, [seriesPanelOpen, seriesRealtime]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const update = () => {
      setIsCompact(window.matchMedia('(max-width: 640px)').matches);
    };
    update();
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('resize', update);
    };
  }, []);

  useEffect(() => {
    if (!hoverInfo) {
      setHoverPosition(null);
      return;
    }
    const update = () => {
      const margin = 12;
      const width = hoverRef.current?.offsetWidth ?? 260;
      const height = hoverRef.current?.offsetHeight ?? 180;
      const maxX = Math.max(margin, window.innerWidth - width - margin);
      const maxY = Math.max(margin, window.innerHeight - height - margin);
      const nextX = clamp(hoverInfo.x + 12, margin, maxX);
      const nextY = clamp(hoverInfo.y + 12, margin, maxY);
      setHoverPosition({ x: nextX, y: nextY });
    };
    update();
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('resize', update);
    };
  }, [hoverInfo]);

  useEffect(() => {
    if (!brokenLinksSorted.length) {
      setBrokenActiveId(null);
      brokenAutoIndexRef.current = 0;
      nextAutoPanAtRef.current = 0;
      return;
    }
    const idx = brokenActiveId
      ? brokenLinksSorted.findIndex(link => link.id === brokenActiveId)
      : -1;
    if (idx < 0) {
      brokenAutoIndexRef.current = 0;
      setBrokenActiveId(brokenLinksSorted[0].id);
      return;
    }
    brokenAutoIndexRef.current = idx;
  }, [brokenActiveId, brokenLinksSorted]);

  useEffect(() => {
    if (!brokenLinksSorted.length || !autoPanEnabled || !mapReady) return;
    if (seriesPanelOpen) return;
    if (trafficEnabled && !trafficReady) return;
    let intervalId: number | null = null;
    let timeoutId: number | null = null;
    const attemptStart = () => {
      if (!autoPanEnabledRef.current) return;
      const now = Date.now();
      const stableFor = now - brokenSignatureChangedAtRef.current;
      if (stableFor < BROKEN_CAROUSEL_STABLE_MS) {
        timeoutId = window.setTimeout(attemptStart, BROKEN_CAROUSEL_STABLE_MS - stableFor);
        return;
      }
      if (!lastAutoPanRef.current || now - lastAutoPanRef.current > BROKEN_CAROUSEL_IDLE_MS) {
        const first = brokenLinksSorted[0];
        if (first) {
          if (DEBUG_AUTOPAN) {
            console.info('auto-pan start', {
              id: first.id,
              status: first.status,
              brokenCount: brokenLinksSorted.length
            });
          }
          setBrokenActiveId(first.id);
          nextAutoPanAtRef.current = now + BROKEN_CAROUSEL_INTERVAL_MS;
        }
      }
    };
    attemptStart();
    const tick = () => {
      const now = Date.now();
      const stableFor = now - brokenSignatureChangedAtRef.current;
      if (stableFor < BROKEN_CAROUSEL_STABLE_MS) return;
      if (nextAutoPanAtRef.current && now < nextAutoPanAtRef.current) return;
      const length = brokenLinksSorted.length;
      if (!length) return;
      let next = brokenAutoIndexRef.current + 1;
      if (next >= length) next = 0;
      brokenAutoIndexRef.current = next;
      const nextLink = brokenLinksSorted[next];
      if (!nextLink) return;
      if (DEBUG_AUTOPAN) {
        console.info('auto-pan tick', {
          index: next,
          id: nextLink.id,
          status: nextLink.status,
          brokenCount: brokenLinksSorted.length
        });
      }
      setBrokenActiveId(nextLink.id);
      nextAutoPanAtRef.current = now + BROKEN_CAROUSEL_INTERVAL_MS;
    };
    intervalId = window.setInterval(tick, BROKEN_CAROUSEL_INTERVAL_MS);
    return () => {
      if (intervalId) {
        window.clearInterval(intervalId);
      }
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [
    autoPanEnabled,
    brokenLinksSorted,
    seriesPanelOpen,
    mapReady,
    trafficEnabled,
    trafficReady
  ]);

  useEffect(() => {
    if (!autoPanEnabled || !mapReady) return;
    if (trafficEnabled && !trafficReady) return;
    if (!brokenActiveId) return;
    const link = brokenLinksSorted.find(entry => entry.id === brokenActiveId);
    if (!link) return;
    if (lastFocusedBrokenIdRef.current === brokenActiveId) return;
    lastFocusedBrokenIdRef.current = brokenActiveId;
    focusOnLink(link, { fromAuto: true });
    lastAutoPanRef.current = Date.now();
  }, [autoPanEnabled, brokenActiveId, brokenLinksSorted, focusOnLink, mapReady, trafficEnabled, trafficReady]);

  useEffect(() => {
    return () => {
      if (flashTimeoutRef.current) {
        window.clearTimeout(flashTimeoutRef.current);
      }
      stopAutoRotate();
    };
  }, [stopAutoRotate]);

  const forwardPacketCandidates = useMemo(() => {
    if (!showHealthy) return { entries: [] as Array<{ link: Link; cumulative: number }>, total: 0 };
    let total = 0;
    const entries: Array<{ link: Link; cumulative: number }> = [];
    for (const link of displayLinks) {
      if (isUnconfirmedTopologyLink(link)) continue;
      if (link.status !== 'healthy') continue;
      const weight = getDirectionalPps(link, 'forward');
      if (weight <= 0) continue;
      total += weight;
      entries.push({ link, cumulative: total });
    }
    return { entries, total };
  }, [displayLinks, getDirectionalPps, showHealthy]);

  const backwardPacketCandidates = useMemo(() => {
    if (!showHealthy) return { entries: [] as Array<{ link: Link; cumulative: number }>, total: 0 };
    let total = 0;
    const entries: Array<{ link: Link; cumulative: number }> = [];
    for (const link of displayLinks) {
      if (isUnconfirmedTopologyLink(link)) continue;
      if (link.status !== 'healthy') continue;
      const weight = getDirectionalPps(link, 'backward');
      if (weight <= 0) continue;
      total += weight;
      entries.push({ link, cumulative: total });
    }
    return { entries, total };
  }, [displayLinks, getDirectionalPps, showHealthy]);

  useEffect(() => {
    packetWeightsForwardRef.current = forwardPacketCandidates;
  }, [forwardPacketCandidates]);

  useEffect(() => {
    packetWeightsBackwardRef.current = backwardPacketCandidates;
  }, [backwardPacketCandidates]);

  const warningForwardPacketCandidates = useMemo(() => {
    if (!showBroken) return { entries: [] as Array<{ link: Link; cumulative: number }>, total: 0 };
    let total = 0;
    const entries: Array<{ link: Link; cumulative: number }> = [];
    for (const link of displayLinks) {
      if (isUnconfirmedTopologyLink(link)) continue;
      if (link.status !== LINK_STATUS_WARNING) continue;
      const weight = getDirectionalPps(link, 'forward');
      if (weight <= 0) continue;
      total += weight;
      entries.push({ link, cumulative: total });
    }
    return { entries, total };
  }, [displayLinks, getDirectionalPps, showBroken]);

  const warningBackwardPacketCandidates = useMemo(() => {
    if (!showBroken) return { entries: [] as Array<{ link: Link; cumulative: number }>, total: 0 };
    let total = 0;
    const entries: Array<{ link: Link; cumulative: number }> = [];
    for (const link of displayLinks) {
      if (isUnconfirmedTopologyLink(link)) continue;
      if (link.status !== LINK_STATUS_WARNING) continue;
      const weight = getDirectionalPps(link, 'backward');
      if (weight <= 0) continue;
      total += weight;
      entries.push({ link, cumulative: total });
    }
    return { entries, total };
  }, [displayLinks, getDirectionalPps, showBroken]);

  useEffect(() => {
    packetWeightsWarningForwardRef.current = warningForwardPacketCandidates;
  }, [warningForwardPacketCandidates]);

  useEffect(() => {
    packetWeightsWarningBackwardRef.current = warningBackwardPacketCandidates;
  }, [warningBackwardPacketCandidates]);

  const brokenPacketCandidates = useMemo(() => {
    if (!showBroken) return { entries: [] as Array<{ link: Link; cumulative: number }>, total: 0 };
    let total = 0;
    const entries: Array<{ link: Link; cumulative: number }> = [];
    for (const link of displayLinks) {
      if (isUnconfirmedTopologyLink(link)) continue;
      if (!isBrokenLikeStatus(link.status)) continue;
      const weight = getBrokenPps(link);
      if (weight <= 0) continue;
      total += weight;
      entries.push({ link, cumulative: total });
    }
    return { entries, total };
  }, [displayLinks, getBrokenPps, showBroken]);

  useEffect(() => {
    packetWeightsBrokenRef.current = brokenPacketCandidates;
  }, [brokenPacketCandidates]);

  const loopRings = useMemo(() => {
    if (!coClusters.length) return [] as LoopRing[];
    const matchesFilter = (status: LinkStatus) =>
      (status === 'healthy' && showHealthy) || (isProblemStatus(status) && showBroken);
    const rings: LoopRing[] = [];
    coClusters.forEach(cluster => {
      const sorted = [...cluster.links]
        .filter(link => !isUnconfirmedTopologyLink(link) && matchesFilter(link.status))
        .sort((a, b) => a.id.localeCompare(b.id));
      if (!sorted.length) return;
      const representative = representativeClusterLink(sorted);
      const radius = CO_LOOP_RADIUS_METERS;
      rings.push({
        id: cluster.id,
        center: cluster.center,
        radius,
        status: aggregateLinkStatus(sorted),
        pps: sorted.reduce((total, link) => total + getLoopPps(link), 0),
        path: buildCirclePath(cluster.center, radius, CO_LOOP_SEGMENTS),
        link: representative,
        links: sorted
      });
    });
    return rings;
  }, [coClusters, getLoopPps, showBroken, showHealthy]);

  const loopRingLines = useMemo(() => {
    if (!loopRings.length)
      return [] as Array<{ id: string; path: Array<[number, number]>; status: LinkStatus; link?: Link }>;
    return loopRings.flatMap(ring => {
      const innerRadius = Math.max(0.5, ring.radius - CO_LOOP_LANE_OFFSET_METERS);
      const outerRadius = ring.radius + CO_LOOP_LANE_OFFSET_METERS;
      return [
        {
          id: `${ring.id}-inner`,
          path: buildCirclePath(ring.center, innerRadius, CO_LOOP_SEGMENTS),
          status: ring.status,
          link: ring.link
        },
        {
          id: `${ring.id}-outer`,
          path: buildCirclePath(ring.center, outerRadius, CO_LOOP_SEGMENTS),
          status: ring.status,
          link: ring.link
        }
      ];
    });
  }, [loopRings]);

  const loopPacketCandidates = useMemo(() => {
    let total = 0;
    const entries: Array<{ ring: LoopRing; direction: 1 | -1; cumulative: number }> = [];
    for (const ring of loopRings) {
      if (!ring.link) continue;
      const ringLinks = ring.links?.length ? ring.links : [ring.link];
      const forward = ringLinks.reduce((sum, link) => sum + getDirectionalPps(link, 'forward'), 0);
      const backward = ringLinks.reduce((sum, link) => sum + getDirectionalPps(link, 'backward'), 0);
      if (forward > 0) {
        total += forward;
        entries.push({ ring, direction: 1, cumulative: total });
      }
      if (backward > 0) {
        total += backward;
        entries.push({ ring, direction: -1, cumulative: total });
      }
    }
    return { entries, total };
  }, [getDirectionalPps, loopRings]);

  useEffect(() => {
    loopPacketWeightsRef.current = loopPacketCandidates;
  }, [loopPacketCandidates]);

  useEffect(() => {
    const tick = (time: number) => {
      if (time - packetLastTickRef.current > 33) {
        setNowSec(time / 1000);
        packetLastTickRef.current = time;
      }
      animationFrameRef.current = requestAnimationFrame(tick);
    };

    animationFrameRef.current = requestAnimationFrame(tick);
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const frame = packetEmissionFrame(nowSec, lastEmitRef.current);
    lastEmitRef.current = frame.lastEmitSec;
    const { delta } = frame;
    if (delta <= 0) return;

    const packetsToAdd: Packet[] = [];
    const loopPacketsToAdd: LoopPacket[] = [];
    const emit = (
      direction: PacketDirection,
      status: LinkStatus,
      weightsRef: React.MutableRefObject<{ entries: Array<{ link: Link; cumulative: number }>; total: number }>,
      ratePerSec: number
    ) => {
      const { entries, total } = weightsRef.current;
      if (!entries.length || total <= 0 || ratePerSec <= 0) return;
      const count = samplePoisson(ratePerSec * delta);
      if (count <= 0) return;
      for (let i = 0; i < count; i += 1) {
        const roll = Math.random() * total;
        const selected = entries.find(entry => roll <= entry.cumulative)?.link ?? entries[0].link;
        packetsToAdd.push({
          id: packetIdRef.current++,
          source: selected.renderSource ?? selected.source,
          target: selected.renderTarget ?? selected.target,
          renderHeight: selected.renderHeight,
          direction,
          status,
          startTime: nowSec,
          endTime: nowSec + PACKET_TRAVEL_SEC
        });
      }
    };

    const emitLoop = (ratePerSec: number) => {
      const { entries, total } = loopPacketWeightsRef.current;
      if (!entries.length || total <= 0 || ratePerSec <= 0) return;
      const count = samplePoisson(ratePerSec * delta);
      if (count <= 0) return;
      for (let i = 0; i < count; i += 1) {
        const roll = Math.random() * total;
        const selected = entries.find(entry => roll <= entry.cumulative) ?? entries[0];
        loopPacketsToAdd.push({
          id: packetIdRef.current++,
          center: selected.ring.center,
          radius: selected.ring.radius,
          direction: selected.direction,
          status: selected.ring.status,
          phase: Math.random(),
          startTime: nowSec,
          endTime: nowSec + PACKET_TRAVEL_SEC
        });
      }
    };

    const forwardTotalPps = packetWeightsForwardRef.current.total;
    const backwardTotalPps = packetWeightsBackwardRef.current.total;
    const warningForwardTotalPps = packetWeightsWarningForwardRef.current.total;
    const warningBackwardTotalPps = packetWeightsWarningBackwardRef.current.total;
    const brokenTotalPps = packetWeightsBrokenRef.current.total;
    const loopTotalPps = loopPacketWeightsRef.current.total;

    let forwardRate = forwardTotalPps / PACKET_REPRESENTS_PPS;
    let backwardRate = backwardTotalPps / PACKET_REPRESENTS_PPS;
    let warningForwardRate = warningForwardTotalPps / PACKET_REPRESENTS_PPS;
    let warningBackwardRate = warningBackwardTotalPps / PACKET_REPRESENTS_PPS;
    let brokenRate = trafficEnabled ? brokenTotalPps / PACKET_REPRESENTS_PPS : PACKET_RATE_BROKEN_PER_SEC;
    let loopRate = loopTotalPps / PACKET_REPRESENTS_PPS;

    const rateScale = packetRateScale;
    forwardRate *= rateScale;
    backwardRate *= rateScale;
    warningForwardRate *= rateScale;
    warningBackwardRate *= rateScale;
    brokenRate *= rateScale;
    loopRate *= rateScale;

    const maxRate = PACKET_MAX_ACTIVE / PACKET_TRAVEL_SEC;
    const totalRate = forwardRate + backwardRate + warningForwardRate + warningBackwardRate + brokenRate * 2 + loopRate;
    if (totalRate > maxRate && totalRate > 0) {
      const scale = maxRate / totalRate;
      forwardRate *= scale;
      backwardRate *= scale;
      warningForwardRate *= scale;
      warningBackwardRate *= scale;
      brokenRate *= scale;
      loopRate *= scale;
    }

    emit('forward', 'healthy', packetWeightsForwardRef, forwardRate);
    emit('backward', 'healthy', packetWeightsBackwardRef, backwardRate);
    emit('forward', LINK_STATUS_WARNING, packetWeightsWarningForwardRef, warningForwardRate);
    emit('backward', LINK_STATUS_WARNING, packetWeightsWarningBackwardRef, warningBackwardRate);
    emit('forward', 'broken', packetWeightsBrokenRef, brokenRate);
    emit('backward', 'broken', packetWeightsBrokenRef, brokenRate);
    emitLoop(loopRate);

    if (packetsToAdd.length || loopPacketsToAdd.length) {
      setPacketFlights(prev =>
        admitPacketCandidates(
          prev,
          { packets: packetsToAdd, loopPackets: loopPacketsToAdd },
          PACKET_MAX_ACTIVE,
          nowSec
        )
      );
    }
  }, [nowSec, packetRateScale, trafficEnabled]);

  useEffect(() => {
    if (nowSec - lastPruneRef.current < 1) return;
    lastPruneRef.current = nowSec;
    setPacketFlights(prev => {
      const packets = retainActivePackets(prev.packets, nowSec);
      const loopPackets = retainActivePackets(prev.loopPackets, nowSec);
      if (packets.length === prev.packets.length && loopPackets.length === prev.loopPackets.length) return prev;
      return { packets, loopPackets, preferLoopNext: prev.preferLoopNext };
    });
  }, [nowSec]);

  const healthyPackets = useMemo(
    () => packets.filter(packet => packet.status === 'healthy'),
    [packets]
  );

  const warningPackets = useMemo(
    () => packets.filter(packet => packet.status === LINK_STATUS_WARNING),
    [packets]
  );

  const brokenPackets = useMemo(
    () => packets.filter(packet => isBrokenLikeStatus(packet.status)),
    [packets]
  );

  const loopDots = useMemo(() => {
    if (!loopPackets.length) return [] as Array<{ id: number; position: [number, number]; status: LinkStatus }>;
    const dots: Array<{ id: number; position: [number, number]; status: LinkStatus }> = [];
    loopPackets.forEach(packet => {
      if (packet.status === 'healthy' && !showHealthy) return;
      if (isProblemStatus(packet.status) && !showBroken) return;
      const duration = Math.max(0.001, packet.endTime - packet.startTime);
      const t = (nowSec - packet.startTime) / duration;
      if (t < 0 || t > 1) return;
      const progress = (t + packet.phase) % 1;
      const angle = progress * Math.PI * 2 * packet.direction;
      const laneOffset = packet.direction > 0 ? CO_LOOP_LANE_OFFSET_METERS : -CO_LOOP_LANE_OFFSET_METERS;
      const laneRadius = Math.max(0.5, packet.radius + laneOffset);
      dots.push({
        id: packet.id,
        position: offsetByMeters(packet.center, laneRadius, angle),
        status: packet.status
      });
    });
    return dots;
  }, [loopPackets, nowSec, showBroken, showHealthy]);

  const endpointPaths = useMemo(() => {
    if (!showEndpointDebug) return [] as Array<{ id: string; path: Array<[number, number]> }>;
    return displayLinks.map(link => ({
      id: `endpoint-${link.id}`,
      path: [link.renderSource ?? link.source, link.renderTarget ?? link.target]
    }));
  }, [displayLinks, showEndpointDebug]);

  const endpointMarkers = useMemo(() => {
    if (!showEndpointDebug) return [] as Array<{ id: string; position: [number, number]; kind: 'source' | 'target' }>;
    const markers: Array<{ id: string; position: [number, number]; kind: 'source' | 'target' }> = [];
    displayLinks.forEach(link => {
      const source = link.renderSource ?? link.source;
      const target = link.renderTarget ?? link.target;
      markers.push({ id: `${link.id}-source`, position: source, kind: 'source' });
      markers.push({ id: `${link.id}-target`, position: target, kind: 'target' });
    });
    return markers;
  }, [displayLinks, showEndpointDebug]);

  const debugLoopRings = useMemo(() => {
    if (!DEBUG_CO_RINGS) return [] as LoopRing[];
    return DEBUG_CO_RADII_METERS.map(radius => ({
      id: `debug-co-${radius}`,
      center: DEBUG_CO_CENTER,
      radius,
      status: 'healthy' as const,
      pps: 0,
      path: buildCirclePath(DEBUG_CO_CENTER, radius, CO_LOOP_SEGMENTS),
      link: undefined
    }));
  }, []);

  const debugLoopDots = useMemo(() => {
    if (!DEBUG_CO_RINGS) return [] as Array<{ position: [number, number]; status: LinkStatus }>;
    const angles = [0, Math.PI / 2, Math.PI, (3 * Math.PI) / 2];
    const dots: Array<{ position: [number, number]; status: LinkStatus }> = [];
    angles.forEach(angle => {
      DEBUG_CO_RADII_METERS.forEach(radius => {
        dots.push({ position: offsetByMeters(DEBUG_CO_CENTER, radius, angle), status: 'healthy' });
      });
    });
    return dots;
  }, []);

  const hubDeviceLayers = useMemo(
    () =>
      buildHubDeviceHealthLayers({
        data: hubDeviceData,
        selectedHubID: hubDeviceDrawer.hub?.hubID,
        palette: deviceHealthPalette,
        zoom: mapZoom
      }),
    [
      deviceHealthPalette,
      hubDeviceData,
      hubDeviceDrawer.hub?.hubID,
      mapZoom
    ]
  );

  const arcLayers = useMemo(() => {
    const layers: Layer[] = [];
    const nowMs = Date.now();
    const selectedId = selectedLink?.id;
    const activeFlashId =
      flashLinkId ?? brokenActiveId ?? (selectedLink && isProblemStatus(selectedLink.status) ? selectedLink.id : null);
    const flashActive = activeFlashId && Math.floor(nowMs / 250) % 2 === 0;
    const selectedPulseActive = selectedId && Math.floor(nowMs / 300) % 2 === 0;
    const missingPulse =
      0.55 + 0.45 * Math.sin((nowMs / LINK_MISSING_PULSE_MS) * Math.PI * 2);
    const getLinkColor = (link: Link | undefined, status: LinkStatus, alpha: number) => {
      const fade = getFadeScale(link?.updated_at_ms, nowMs);
      const isMissing = status === LINK_STATUS_MISSING;
      const base = isMissing
        ? LINK_COLOR_MISSING
        : status === LINK_STATUS_WARNING
          ? LINK_COLOR_WARNING
        : status === 'broken'
          ? LINK_COLOR_BROKEN
          : getUtilColor(link ? getLinkUtilization(link) : null);
      const boosted = link?.id && selectedId && link.id === selectedId ? Math.min(255, alpha + 40) : alpha;
      const pulseScale = isMissing ? missingPulse : 1;
      const opticScale = status === 'healthy' && link ? getLinkOpticBrightness(link) ?? 1 : 1;
      return [base[0], base[1], base[2], scaleAlpha(boosted, fade * pulseScale * opticScale)] as [
        number,
        number,
        number,
        number
      ];
    };
    const getPacketColor = (status: LinkStatus) => {
      const isMissing = status === LINK_STATUS_MISSING;
      const base =
        status === LINK_STATUS_WARNING
          ? PACKET_COLOR_WARNING
          : status === 'broken'
          ? PACKET_COLOR_BROKEN
          : isMissing
            ? PACKET_COLOR_MISSING
            : PACKET_COLOR_HEALTHY;
      const pulseScale = isMissing ? missingPulse : 1;
      return [base[0], base[1], base[2], scaleAlpha(base[3], pulseScale)] as [number, number, number, number];
    };
    if (DEBUG_ONLY) {
      if (debugLoopRings.length > 0) {
        layers.push(
          new PathLayer({
            id: 'network-co-rings',
            data: debugLoopRings,
            getPath: ring => ring.path,
            getColor: ring => getLinkColor(ring.link, ring.status, LINK_RING_ALPHA),
            widthUnits: 'pixels',
            getWidth: CO_RING_WIDTH,
            widthMinPixels: 1,
            widthMaxPixels: 3,
            jointRounded: true,
            capRounded: true,
            pickable: false
          })
        );
      }
    if (debugLoopDots.length > 0) {
      layers.push(
        new ScatterplotLayer({
          id: 'network-co-packets',
          data: debugLoopDots,
          getPosition: dot => dot.position,
          getFillColor: dot => getPacketColor(dot.status),
          getRadius: CO_DOT_RADIUS_METERS,
          radiusUnits: 'meters',
          pickable: false
        })
      );
    }
      if (DEBUG_CO_RINGS) {
        layers.push(
          new ScatterplotLayer({
            id: 'network-co-debug-center',
            data: [{ position: DEBUG_CO_CENTER }],
            getPosition: d => d.position,
            getFillColor: DEBUG_CENTER_COLOR,
            getRadius: CO_DOT_RADIUS * 1.6 * pixelRatio,
            radiusUnits: 'pixels',
            radiusMinPixels: CO_DOT_RADIUS_MIN * 1.6 * pixelRatio,
            radiusMaxPixels: CO_DOT_RADIUS_MAX * 1.6 * pixelRatio,
            pickable: false
          })
        );
      }
      return layers;
    }
    const fallbackLinks = displayLinks.filter(link => {
      if (isUnconfirmedTopologyLink(link)) return false;
      if (link.status === 'healthy' && !showHealthy) return false;
      if (isProblemStatus(link.status) && !showBroken) return false;
      return true;
    });

    if (fallbackLinks.length > 0) {
      layers.push(
        new ArcLayer({
          id: 'network-arc-pipe',
          data: fallbackLinks,
          getSourcePosition: link => toArcPosition(link.renderSource ?? link.source),
          getTargetPosition: link => toArcPosition(link.renderTarget ?? link.target),
          getSourceColor: link => {
            const fade = getFadeScale(link?.updated_at_ms, nowMs);
            return [
              LINK_CAPACITY_COLOR[0],
              LINK_CAPACITY_COLOR[1],
              LINK_CAPACITY_COLOR[2],
              scaleAlpha(LINK_CAPACITY_ALPHA, fade)
            ];
          },
          getTargetColor: link => {
            const fade = getFadeScale(link?.updated_at_ms, nowMs);
            return [
              LINK_CAPACITY_COLOR[0],
              LINK_CAPACITY_COLOR[1],
              LINK_CAPACITY_COLOR[2],
              scaleAlpha(LINK_CAPACITY_ALPHA, fade)
            ];
          },
          getWidth: link => getArcPipeWidthForLink(link, links),
          getHeight: link => link.renderHeight ?? ARC_HEIGHT,
          getTilt: 0,
          widthUnits: 'pixels',
          widthMinPixels: ARC_PIPE_WIDTH_MIN,
          widthMaxPixels: ARC_PIPE_WIDTH_MAX,
          greatCircle: ARC_GREAT_CIRCLE,
          numSegments: ARC_SEGMENTS,
          pickable: false
        })
      );
      layers.push(
        new ArcLayer({
          id: 'network-arc-lines',
          data: fallbackLinks,
          getSourcePosition: link => toArcPosition(link.renderSource ?? link.source),
          getTargetPosition: link => toArcPosition(link.renderTarget ?? link.target),
          getSourceColor: link =>
            getLinkColor(link, link.status, isBrokenLikeStatus(link.status) ? LINK_LINE_ALPHA_BROKEN : LINK_LINE_ALPHA),
          getTargetColor: link =>
            getLinkColor(link, link.status, isBrokenLikeStatus(link.status) ? LINK_LINE_ALPHA_BROKEN : LINK_LINE_ALPHA),
          getWidth: link => getArcWidthForLink(link, links),
          getHeight: link => link.renderHeight ?? ARC_HEIGHT,
          getTilt: 0,
          widthUnits: 'pixels',
          widthMinPixels: ARC_WIDTH_MIN,
          widthMaxPixels: ARC_WIDTH_MAX,
          greatCircle: ARC_GREAT_CIRCLE,
          numSegments: ARC_SEGMENTS,
          pickable: true,
          autoHighlight: true,
          highlightColor: ARC_HIGHLIGHT_COLOR
        })
      );
    }

    if (flashActive && activeFlashId) {
      const flashLinks = fallbackLinks.filter(link => link.id === activeFlashId);
      if (flashLinks.length > 0) {
        layers.push(
          new ArcLayer({
            id: 'network-arc-lines-flash',
            data: flashLinks,
            getSourcePosition: link => toArcPosition(link.renderSource ?? link.source),
            getTargetPosition: link => toArcPosition(link.renderTarget ?? link.target),
            getSourceColor: () => [255, 50, 60, 240],
            getTargetColor: () => [255, 50, 60, 240],
            getWidth: link => getArcWidthForLink(link, links) + 2,
            getHeight: link => link.renderHeight ?? ARC_HEIGHT,
            getTilt: 0,
            widthUnits: 'pixels',
            widthMinPixels: ARC_WIDTH_MIN + 1,
            widthMaxPixels: ARC_WIDTH_MAX + 4,
            greatCircle: ARC_GREAT_CIRCLE,
            numSegments: ARC_SEGMENTS,
            pickable: false
          })
        );
      }
    }

    if (selectedPulseActive && selectedId) {
      const selectedLinks = fallbackLinks.filter(link => link.id === selectedId);
      if (selectedLinks.length > 0) {
        layers.push(
          new ArcLayer({
            id: 'network-arc-lines-selected',
            data: selectedLinks,
            getSourcePosition: link => toArcPosition(link.renderSource ?? link.source),
            getTargetPosition: link => toArcPosition(link.renderTarget ?? link.target),
            getSourceColor: () => [90, 180, 255, 230],
            getTargetColor: () => [90, 180, 255, 230],
            getWidth: link => getArcWidthForLink(link, links) + 2,
            getHeight: link => link.renderHeight ?? ARC_HEIGHT,
            getTilt: 0,
            widthUnits: 'pixels',
            widthMinPixels: ARC_WIDTH_MIN + 1,
            widthMaxPixels: ARC_WIDTH_MAX + 4,
            greatCircle: ARC_GREAT_CIRCLE,
            numSegments: ARC_SEGMENTS,
            pickable: false
          })
        );
      }
    }

    if (loopRings.length > 0) {
      layers.push(
        new PathLayer({
          id: 'network-co-rings-hit',
          data: loopRings,
          getPath: ring => ring.path,
          getColor: ring =>
            getLinkColor(
              ring.link,
              ring.status,
              isBrokenLikeStatus(ring.status) ? LINK_PIPE_ALPHA + 10 : LINK_PIPE_ALPHA
            ),
          getWidth: CO_LOOP_HIT_WIDTH_METERS,
          widthUnits: 'meters',
          widthMinPixels: 6,
          widthMaxPixels: 20,
          pickable: true,
          autoHighlight: true,
          highlightColor: [255, 255, 255, 160],
          opacity: 1
        })
      );
    }

    if (loopRingLines.length > 0) {
      layers.push(
        new PathLayer({
          id: 'network-co-rings',
          data: loopRingLines,
          getPath: ring => ring.path,
          getColor: ring => getLinkColor(ring.link, ring.status, LINK_RING_ALPHA),
          getWidth: CO_RING_WIDTH_METERS,
          widthUnits: 'meters',
          widthMinPixels: 1,
          widthMaxPixels: 6,
          pickable: false
        })
      );
    }

    if (flashActive && activeFlashId) {
      const flashRings = loopRingLines.filter(ring => ring.link?.id === activeFlashId);
      if (flashRings.length > 0) {
        layers.push(
          new PathLayer({
            id: 'network-co-rings-flash',
            data: flashRings,
            getPath: ring => ring.path,
            getColor: () => [255, 50, 60, 240],
            getWidth: CO_RING_WIDTH_METERS * 1.6,
            widthUnits: 'meters',
            widthMinPixels: 2,
            widthMaxPixels: 10,
            pickable: false
          })
        );
      }
    }

    if (selectedPulseActive && selectedId) {
      const selectedRings = loopRingLines.filter(ring => ring.link?.id === selectedId);
      if (selectedRings.length > 0) {
        layers.push(
          new PathLayer({
            id: 'network-co-rings-selected',
            data: selectedRings,
            getPath: ring => ring.path,
            getColor: () => [90, 180, 255, 230],
            getWidth: CO_RING_WIDTH_METERS * 1.6,
            widthUnits: 'meters',
            widthMinPixels: 2,
            widthMaxPixels: 10,
            pickable: false
          })
        );
      }
    }

    if (loopDots.length > 0) {
      layers.push(
        new ScatterplotLayer({
          id: 'network-co-packets',
          data: loopDots,
          getPosition: dot => dot.position,
          getFillColor: dot => getPacketColor(dot.status),
          getRadius: CO_DOT_RADIUS_METERS,
          radiusUnits: 'meters',
          pickable: false
        })
      );
    }

    if (showEndpointDebug && endpointPaths.length > 0) {
      layers.push(
        new PathLayer({
          id: 'network-debug-endpoint-lines',
          data: endpointPaths,
          getPath: line => line.path,
          getColor: () => [120, 160, 255, 180],
          getWidth: 1,
          widthUnits: 'pixels',
          widthMinPixels: 1,
          widthMaxPixels: 2,
          pickable: false
        })
      );
    }

    if (showEndpointDebug && endpointMarkers.length > 0) {
      layers.push(
        new ScatterplotLayer({
          id: 'network-debug-endpoints',
          data: endpointMarkers,
          getPosition: marker => marker.position,
          getFillColor: marker => (marker.kind === 'source' ? [120, 160, 255, 220] : [255, 120, 200, 220]),
          getRadius: CO_DOT_RADIUS * 1.2 * pixelRatio,
          radiusUnits: 'pixels',
          radiusMinPixels: CO_DOT_RADIUS_MIN * 1.2 * pixelRatio,
          radiusMaxPixels: CO_DOT_RADIUS_MAX * 1.2 * pixelRatio,
          pickable: false
        })
      );
    }

    if (showHealthy && healthyPackets.length > 0) {
      layers.push(
        new AnimatedArcLayer({
          id: 'network-arc-packets-healthy',
          data: healthyPackets,
          getSourcePosition: packet => toArcPosition(packet.direction === 'forward' ? packet.source : packet.target),
          getTargetPosition: packet => toArcPosition(packet.direction === 'forward' ? packet.target : packet.source),
          getSourceTimestamp: packet => packet.startTime,
          getTargetTimestamp: packet => packet.endTime,
          getSourceColor: PACKET_COLOR_HEALTHY,
          getTargetColor: PACKET_COLOR_HEALTHY,
          getWidth: HIGHLIGHT_WIDTH,
          getHeight: packet => packet.renderHeight ?? ARC_HEIGHT,
          getTilt: 0,
          widthUnits: 'pixels',
          widthMinPixels: 3,
          widthMaxPixels: 8,
          greatCircle: ARC_GREAT_CIRCLE,
          numSegments: ARC_SEGMENTS,
          pickable: false,
          timeRange: [nowSec - PACKET_TAIL_SEC, nowSec]
        })
      );
    }

    if (showBroken && warningPackets.length > 0) {
      layers.push(
        new AnimatedArcLayer({
          id: 'network-arc-packets-warning',
          data: warningPackets,
          getSourcePosition: packet => toArcPosition(packet.direction === 'forward' ? packet.source : packet.target),
          getTargetPosition: packet => toArcPosition(packet.direction === 'forward' ? packet.target : packet.source),
          getSourceTimestamp: packet => packet.startTime,
          getTargetTimestamp: packet => packet.endTime,
          getSourceColor: packet => getPacketColor(packet.status),
          getTargetColor: packet => getPacketColor(packet.status),
          getWidth: HIGHLIGHT_WIDTH,
          getHeight: packet => packet.renderHeight ?? ARC_HEIGHT,
          getTilt: 0,
          widthUnits: 'pixels',
          widthMinPixels: 3,
          widthMaxPixels: 8,
          greatCircle: ARC_GREAT_CIRCLE,
          numSegments: ARC_SEGMENTS,
          pickable: false,
          timeRange: [nowSec - PACKET_TAIL_SEC, nowSec]
        })
      );
    }

    if (showBroken && brokenPackets.length > 0) {
      layers.push(
        new AnimatedArcLayer({
          id: 'network-arc-packets-broken',
          data: brokenPackets,
          getSourcePosition: packet => toArcPosition(packet.direction === 'forward' ? packet.source : packet.target),
          getTargetPosition: packet => toArcPosition(packet.direction === 'forward' ? packet.target : packet.source),
          getSourceTimestamp: packet => packet.startTime,
          getTargetTimestamp: packet => packet.endTime,
          getSourceColor: packet => getPacketColor(packet.status),
          getTargetColor: packet => getPacketColor(packet.status),
          getWidth: HIGHLIGHT_WIDTH,
          getHeight: packet => packet.renderHeight ?? ARC_HEIGHT,
          getTilt: 0,
          widthUnits: 'pixels',
          widthMinPixels: 3,
          widthMaxPixels: 8,
          greatCircle: ARC_GREAT_CIRCLE,
          numSegments: ARC_SEGMENTS,
          pickable: false,
          timeRange: [nowSec - PACKET_TAIL_SEC, nowSec],
          fadeEnabled: true,
          fadeMidpoint: 0.5,
          fadeRange: 0.12
        })
      );
    }

    if (DEBUG_CO_RINGS) {
      layers.push(
        new ScatterplotLayer({
          id: 'network-co-debug-center',
          data: [{ position: DEBUG_CO_CENTER }],
          getPosition: d => d.position,
          getFillColor: DEBUG_CENTER_COLOR,
          getRadius: CO_DOT_RADIUS * 1.6 * pixelRatio,
          radiusUnits: 'pixels',
          radiusMinPixels: CO_DOT_RADIUS_MIN * 1.6 * pixelRatio,
          radiusMaxPixels: CO_DOT_RADIUS_MAX * 1.6 * pixelRatio,
          pickable: false
        })
      );
    }
    return layers;
  }, [
    brokenPackets,
    debugLoopRings,
    debugLoopDots,
    displayLinks,
    endpointMarkers,
    endpointPaths,
    getLinkOpticBrightness,
    getLinkUtilization,
    healthyPackets,
    links,
    warningPackets,
    loopDots,
    loopRings,
    loopRingLines,
    nowSec,
    pixelRatio,
    flashLinkId,
    selectedLink,
    showBroken,
    showEndpointDebug,
    showHealthy
  ]);

  const deckLayers = useMemo(
    () => [...arcLayers, ...hubDeviceLayers],
    [arcLayers, hubDeviceLayers]
  );

  useEffect(() => {
    if (!mapReady) return;
    mapRef.current?.triggerRepaint();
  }, [mapReady, nowSec]);

  useEffect(() => {
    if (!selectedLink) return;
    const updated = links.find(link => link.id === selectedLink.id);
    if (!updated) {
      closeSeriesDrawer();
      return;
    }
    if (updated !== selectedLink) {
      setSelectedLink(updated);
    }
  }, [closeSeriesDrawer, links, selectedLink]);

  const linkUtil = selectedLink ? getLinkUtilization(selectedLink) : null;
  const linkCapacity = selectedLink ? getCapacityMbps(selectedLink, links) : null;

  const confirmedAlerts = snapshot?.active_alerts ?? [];
  const unverifiedAlerts = snapshot?.unverified_alerts ?? [];
  const alertTotals = snapshot?.alert_totals;
  const alertStateConfirmable =
    snapshot?.cache_metadata.state !== 'stale' &&
    (freshness?.alerting === 'fresh' || freshness?.alerting === 'delayed');

  const activeAlertRail = publicMode ? null : (
    <div
      className="rounded-md border border-border/60 bg-background/95 p-3 shadow-lg backdrop-blur"
      data-testid="active-alert-rail"
    >
      <div className="flex items-center justify-between gap-3">
        <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-foreground">
          Active alerts {alertTotals?.confirmed_total ?? confirmedAlerts.length}
          {alertTotals ? ` · ${alertTotals.critical_count} critical · ${alertTotals.warning_count} warning` : ''}
        </div>
        <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
          {transport} · {freshness?.alerting ?? 'loading'}
        </div>
      </div>
      <div className="mt-2 max-h-40 space-y-1 overflow-y-auto">
        {confirmedAlerts.length ? (
          confirmedAlerts.map(alert => (
            <button
              key={alert.alert_id}
              type="button"
              onClick={() => onFocusAlert(alert.alert_id)}
              className={`block w-full rounded border px-2 py-1.5 text-left transition hover:bg-accent ${
                focusedAlert?.alert_id === alert.alert_id
                  ? 'border-primary/60 bg-primary/10'
                  : 'border-border/30 bg-muted/10'
              }`}
            >
              <span
                className={`mr-2 text-[9px] font-bold uppercase ${
                  alert.severity === 'critical' ? 'text-destructive' : 'text-amber-300'
                }`}
              >
                {alert.severity}
              </span>
              <span className="text-[11px] text-foreground">{alert.summary}</span>
              <span className="mt-0.5 block text-[9px] uppercase tracking-[0.12em] text-muted-foreground">
                {alert.resource_kind}
                {alert.resource_id ? ` · ${alert.resource_id}` : ' · unmapped'}
                {alert.acknowledged ? ' · acknowledged' : ''}
                {alert.suppressed ? ' · suppressed' : ''}
              </span>
            </button>
          ))
        ) : !alertStateConfirmable ? (
          <div className="py-2 text-[11px] text-muted-foreground">
            Current alert status cannot be confirmed.
          </div>
        ) : (
          <div className="py-2 text-[11px] text-muted-foreground">No active alerts.</div>
        )}
        {unverifiedAlerts.length ? (
          <div className="mt-2 border-t border-border/40 pt-2" data-testid="unverified-alert-rail">
            <div className="mb-1 text-[9px] font-semibold uppercase tracking-[0.15em] text-muted-foreground">
              Unverified conditions {alertTotals?.unverified_count ?? unverifiedAlerts.length}
            </div>
            {unverifiedAlerts.map(alert => (
              <button
                key={alert.alert_id}
                type="button"
                onClick={() => onFocusAlert(alert.alert_id)}
                className="mb-1 block w-full rounded border border-border/30 bg-muted/10 px-2 py-1.5 text-left text-muted-foreground transition hover:bg-accent"
              >
                <span className="text-[11px]">{alert.summary}</span>
                <span className="mt-0.5 block text-[9px] uppercase tracking-[0.12em]">
                  {alert.verification_reason || 'source unavailable'}
                  {alert.last_confirmed_at ? ` · last confirmed ${new Date(alert.last_confirmed_at).toLocaleString()}` : ''}
                </span>
              </button>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );

  if (styleError || (loadError && !snapshot)) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
        Unable to load network map.
      </div>
    );
  }

  return (
    <div
      className="relative flex h-full w-full overflow-hidden"
      data-packet-layer-present={arcLayers.some(layer => layer.id.includes('packets')) ? 'true' : 'false'}
      data-packet-mode={packetsEstimated ? 'estimated' : 'live'}
      data-packet-count={packets.length + loopPackets.length}
      data-device-marker-count={hubDeviceData.length}
      data-hub-status-radius={hubDeviceStatusRadiusForZoom(mapZoom)}
      data-read-only={kioskMode || publicMode ? 'true' : 'false'}
      data-testid="status-telemetry-map"
    >
      <div className="relative h-full min-w-0 flex-1 transition-[width] duration-300 ease-out">
        <div ref={divRef} className="h-full w-full" />
        <MapLibreDeckOverlay
          map={mapReady ? mapRef.current : null}
          layers={deckLayers}
          onHover={handleHover}
          onClick={handleClick}
        />
      <div className="pointer-events-none absolute left-4 top-4 z-10 hidden md:flex flex-col gap-1">
        <img
          src="/acd-logo.webp"
          alt="ACD"
          className="h-auto max-h-12 w-auto max-w-[160px] opacity-95 drop-shadow"
        />
        <div className="text-[11px] font-semibold uppercase tracking-[0.35em] text-emerald-200/90 drop-shadow-[0_0_10px_rgba(52,211,153,0.6)]">
          {publicMode ? 'Public network status' : 'Real-time network telemetry'}
        </div>
      </div>
      {!brokenLinksSorted.length ? (
        <div className="pointer-events-auto absolute bottom-4 left-4 z-20 hidden w-[min(25rem,calc(100%-2rem))] md:block">
          {activeAlertRail}
        </div>
      ) : null}
      <div className="pointer-events-auto absolute right-4 top-4 z-10 hidden flex-col gap-2 md:flex">
        <div className="flex flex-wrap items-center gap-2 rounded-md border border-border/60 bg-background/95 px-3 py-2 text-[11px] uppercase tracking-[0.2em] text-foreground shadow-lg backdrop-blur">
          <span className="text-emerald-200">Up {linkCounts.healthy}</span>
          <span className="text-amber-200">Warn {linkCounts.warning}</span>
          <span className="text-destructive">Down {linkCounts.broken}</span>
          <span className="text-cyan-100">Hubs {mapHubs.length}</span>
          {publicMode ? (
            <span className="text-muted-foreground">Approximate locations · {freshness?.telemetry ?? transport}</span>
          ) : (
            <>
              <span className={getTrafficAgeTone(trafficUpdatedAtMs, trafficEnabled)}>
                Telemetry {trafficEnabled && trafficUpdatedAtMs ? formatAge(trafficUpdatedAtMs) : '—'}
              </span>
              <span className={packetsEstimated ? 'text-amber-300' : 'text-emerald-300'}>
                Packets {packetsEstimated ? 'estimated' : 'live'} · Stream {freshness?.telemetry ?? transport}
              </span>
              <span className="text-muted-foreground">
                Fetch {formatAge(lastTrafficQueryRef.current || undefined)}
              </span>
            </>
          )}
        </div>
        {!publicMode ? <div className="rounded-md border border-border/60 bg-background/95 px-3 py-2 text-[11px] text-muted-foreground shadow-lg backdrop-blur">
          <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.2em] text-foreground">
            <span>Packet rate</span>
            <span className="text-muted-foreground">{packetRateScale.toFixed(2)}x</span>
          </div>
          <div className="mt-2 flex items-center gap-2">
            <input
              type="range"
              min={PACKET_RATE_SCALE_MIN}
              max={PACKET_RATE_SCALE_MAX}
              step={PACKET_RATE_SCALE_STEP}
              value={packetRateScale}
              onChange={event => setPacketRateScale(Number(event.target.value))}
              aria-label="Packet rate"
              className="w-full accent-emerald-400"
            />
            <span className="min-w-[3ch] text-[10px] text-muted-foreground">
              {Math.round(packetRateScale * 100)}%
            </span>
          </div>
        </div> : null}
      </div>
      <div className={`pointer-events-auto absolute right-4 z-10 flex flex-col items-end gap-2 ${brokenLinksSorted.length ? 'bottom-20 sm:bottom-4' : 'bottom-4'}`}>
        <div className="overflow-hidden rounded-md border border-border/60 bg-background/95 shadow-lg backdrop-blur">
          <button
            type="button"
            onClick={handleZoomIn}
            disabled={!mapReady}
            className="flex h-10 w-10 items-center justify-center text-lg text-foreground transition disabled:cursor-not-allowed disabled:opacity-40"
            aria-label="Zoom in"
            title="Zoom in"
          >
            +
          </button>
          <div className="h-px bg-border/60" />
          <button
            type="button"
            onClick={handleZoomOut}
            disabled={!mapReady}
            className="flex h-10 w-10 items-center justify-center text-lg text-foreground transition disabled:cursor-not-allowed disabled:opacity-40"
            aria-label="Zoom out"
            title="Zoom out"
          >
            −
          </button>
        </div>
        <button
          type="button"
          onClick={() => setAutoPanEnabled(value => !value)}
          className={
            autoPanEnabled
              ? 'rounded-md border border-emerald-400/60 bg-emerald-400/10 px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-emerald-200 shadow-lg backdrop-blur sm:text-xs'
              : 'rounded-md border border-border/60 bg-background/95 px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-foreground shadow-lg backdrop-blur sm:text-xs'
          }
        >
          {autoPanEnabled ? 'Auto Camera' : 'Free Camera'}
        </button>
      </div>
      {brokenLinksSorted.length ? (() => {
        const link = brokenActiveLink ?? brokenLinksSorted[0];
        const traffic = link ? trafficById[link.id] : undefined;
        const util = link ? getLinkUtilization(link) : null;
        const statusBadgeClass =
          !link || link.status === 'broken'
            ? 'border-red-400/30 bg-red-400/10 text-red-300'
            : link.status === LINK_STATUS_WARNING
              ? 'border-amber-400/30 bg-amber-400/10 text-amber-300'
              : link.status === LINK_STATUS_MISSING
                ? 'border-violet-400/30 bg-violet-400/10 text-violet-300'
                : 'border-emerald-400/30 bg-emerald-400/10 text-emerald-300';
        const statusLabel =
          !link || link.status === 'broken' ? 'Down'
            : link.status === LINK_STATUS_WARNING ? 'Warning'
            : link.status === LINK_STATUS_MISSING ? 'Missing Data'
            : 'Healthy';
        const severityDotClass =
          brokenLinksSorted.some(l => l.status === 'broken')
            ? 'animate-pulse bg-red-400 shadow-[0_0_5px_1px_rgba(248,113,113,0.6)]'
            : brokenLinksSorted.some(l => l.status === LINK_STATUS_WARNING)
              ? 'bg-amber-400 shadow-[0_0_5px_1px_rgba(251,191,36,0.5)]'
              : 'bg-violet-400 shadow-[0_0_5px_1px_rgba(167,139,250,0.5)]';
        const statusAccent =
          !link || link.status === 'broken' ? 'border-l-red-400'
            : link.status === LINK_STATUS_WARNING ? 'border-l-amber-400'
            : link.status === LINK_STATUS_MISSING ? 'border-l-violet-400'
            : 'border-l-emerald-400';

        const navControls = (
          <>
            <button
              type="button"
              onClick={() => navigateBrokenLink('prev')}
              disabled={brokenLinksSorted.length < 2}
              className="flex h-6 w-6 items-center justify-center rounded border border-border/30 bg-muted/15 text-muted-foreground transition hover:bg-muted/35 hover:text-foreground disabled:cursor-not-allowed disabled:opacity-30"
              aria-label="Previous problem link"
            >
              <svg viewBox="0 0 8 12" fill="none" stroke="currentColor" strokeWidth="2" className="h-2.5 w-2.5">
                <path d="M6 1L2 6l4 5" />
              </svg>
            </button>
            <span className="min-w-[2.5rem] text-center text-[10px] font-semibold tabular-nums text-foreground/75">
              {brokenActiveIndex + 1}/{brokenLinksSorted.length}
            </span>
            <button
              type="button"
              onClick={() => navigateBrokenLink('next')}
              disabled={brokenLinksSorted.length < 2}
              className="flex h-6 w-6 items-center justify-center rounded border border-border/30 bg-muted/15 text-muted-foreground transition hover:bg-muted/35 hover:text-foreground disabled:cursor-not-allowed disabled:opacity-30"
              aria-label="Next problem link"
            >
              <svg viewBox="0 0 8 12" fill="none" stroke="currentColor" strokeWidth="2" className="h-2.5 w-2.5">
                <path d="M2 1l4 5-4 5" />
              </svg>
            </button>
          </>
        );

        const detailContent = link ? (
          <div className="grid grid-cols-2 gap-x-4 gap-y-3">
            {/* Endpoints */}
            <div className="col-span-2 grid grid-cols-2 gap-2">
              {[
                { label: 'A End', host: link.telemetry_host_a, iface: link.telemetry_ifname_a, color: 'var(--color-info)' },
                { label: 'Z End', host: link.telemetry_host_b, iface: link.telemetry_ifname_b, color: 'var(--color-warning)' }
              ].map(ep => (
                <div key={ep.label} className="rounded-lg border border-border/20 px-2.5 py-2" style={{ borderLeftColor: ep.color, borderLeftWidth: '2px' }}>
                  <div className="text-[8px] font-bold uppercase tracking-[0.18em]" style={{ color: ep.color }}>
                    {ep.label}
                  </div>
                  <div className="mt-0.5 truncate text-[10px] font-semibold text-foreground/90">
                    {ep.host ?? '—'}
                  </div>
                  <div className="truncate text-[9px] text-muted-foreground/65">
                    {ep.iface ?? '—'}
                  </div>
                </div>
              ))}
            </div>

            {/* Capacity */}
            <div>
              <div className="text-[8px] font-semibold uppercase tracking-[0.2em] text-muted-foreground/55">
                Capacity
              </div>
              <div className="mt-0.5 text-[14px] font-bold text-foreground">
                {formatCapacityMbps(getCapacityMbps(link, links))}
              </div>
            </div>

            {/* Utilization */}
            <div>
              <div className="text-[8px] font-semibold uppercase tracking-[0.2em] text-muted-foreground/55">
                Utilization
              </div>
              <div className={
                'mt-0.5 text-[14px] font-bold ' + (
                  !Number.isFinite(util) ? 'text-muted-foreground'
                    : (util as number) >= LINK_UTIL_CRIT ? 'text-red-400'
                    : (util as number) >= LINK_UTIL_WARN ? 'text-amber-400'
                    : 'text-emerald-400'
                )
              }>
                {formatUtilization(util)}
              </div>
            </div>

            {/* Traffic rates */}
            {traffic ? (
              <div className="col-span-2">
                <div className="mb-1 text-[8px] font-semibold uppercase tracking-[0.2em] text-muted-foreground/55">
                  Traffic
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-1.5 text-[10px]">
                    <span className="h-1 w-2.5 shrink-0 rounded-full" style={{ background: 'var(--color-info)' }} />
                    <span className="text-muted-foreground/70">A</span>
                    <span className="tabular-nums text-foreground/90">{formatMbps(traffic.source_bps)}</span>
                    {traffic.source_pps ? <span className="text-[9px] text-muted-foreground/50">{formatPps(traffic.source_pps)}</span> : null}
                  </div>
                  <div className="flex items-center gap-1.5 text-[10px]">
                    <span className="h-1 w-2.5 shrink-0 rounded-full" style={{ background: 'var(--color-warning)' }} />
                    <span className="text-muted-foreground/70">Z</span>
                    <span className="tabular-nums text-foreground/90">{formatMbps(traffic.target_bps)}</span>
                    {traffic.target_pps ? <span className="text-[9px] text-muted-foreground/50">{formatPps(traffic.target_pps)}</span> : null}
                  </div>
                </div>
              </div>
            ) : null}

            {/* Status reason */}
            {(link.status === LINK_STATUS_WARNING || link.status === 'broken') && link.status_reason?.summary ? (
              <div className="col-span-2 flex gap-2">
                <div className="mt-[3px] h-1.5 w-1.5 shrink-0 rounded-full bg-amber-400" />
                <span className="text-[10px] leading-snug text-amber-100/85">
                  {link.status_reason.summary}
                </span>
              </div>
            ) : null}

            {/* Traffic age */}
            <div className="col-span-2 text-[9px] text-muted-foreground/45">
              Traffic {trafficEnabled && trafficUpdatedAtMs ? formatAge(trafficUpdatedAtMs) : '—'}
            </div>
          </div>
        ) : null;

        return (
          <>
            {/* Mobile bottom bar (hidden on md+) */}
            <div className="pointer-events-auto absolute bottom-0 left-0 right-0 z-20 md:hidden">

              {/* Expanded detail panel */}
              {link && brokenPanelExpanded ? (
                <div className="max-h-[45vh] overflow-y-auto border-t border-border/35 bg-background/96 backdrop-blur-md thin-scrollbar">
                  <div className="px-4 py-3">
                    {DEBUG_AUTOPAN ? (
                      <div className="mb-2 rounded border border-border/40 bg-muted/30 px-2 py-1 text-[8px] uppercase tracking-[0.15em] text-muted-foreground">
                        {debugAutoPanInfo || 'auto-pan debug'}
                      </div>
                    ) : null}
                    {detailContent}
                  </div>
                </div>
              ) : null}

              {/* Two-row slim bar */}
              <div className="border-t border-border/35 bg-background/96 px-4 py-2 backdrop-blur-md">
                {/* Row 1: severity label (left) + controls (right) */}
                <div className="flex items-center gap-2">
                  <div className={`h-1.5 w-1.5 shrink-0 rounded-full ${severityDotClass}`} />
                  <span className="text-[9px] font-bold uppercase tracking-[0.22em] text-muted-foreground/70">
                    Problems
                  </span>
                  <div className="ml-auto flex shrink-0 items-center gap-1.5">
                    {navControls}
                    <button
                      type="button"
                      onClick={() => setBrokenPanelExpanded(v => !v)}
                      className="flex h-6 w-6 items-center justify-center rounded border border-border/30 bg-muted/15 text-muted-foreground transition hover:bg-muted/35 hover:text-foreground"
                      aria-label={brokenPanelExpanded ? 'Collapse' : 'Expand'}
                    >
                      <svg
                        viewBox="0 0 10 6"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        className={`h-2 w-2.5 transition-transform duration-200 ${brokenPanelExpanded ? 'rotate-180' : ''}`}
                      >
                        <path d="M1 5l4-4 4 4" />
                      </svg>
                    </button>
                    {link ? (
                      <button
                        type="button"
                        onClick={() => openSeriesDrawer(link)}
                        className="flex h-6 items-center gap-1 rounded border border-border/30 bg-muted/15 px-2 text-[9px] font-semibold text-muted-foreground transition hover:bg-muted/35 hover:text-foreground"
                        title="View link details"
                      >
                        View
                        <svg viewBox="0 0 6 10" fill="none" stroke="currentColor" strokeWidth="2" className="h-2 w-2">
                          <path d="M1 1l4 4-4 4" />
                        </svg>
                      </button>
                    ) : null}
                  </div>
                </div>
                {/* Row 2: link ID + status badge */}
                {link ? (
                  <div className="mt-1 flex min-w-0 items-center gap-2">
                    <span className="truncate text-[11px] font-bold text-foreground">{link.id}</span>
                    <span className={`shrink-0 rounded border px-1.5 py-0.5 text-[8px] font-bold uppercase tracking-[0.12em] ${statusBadgeClass}`}>
                      {statusLabel}
                    </span>
                  </div>
                ) : null}
              </div>
            </div>

            {/* Desktop panels share one bounded stack so they cannot overlap. */}
            <div
              className="pointer-events-none absolute bottom-4 left-4 z-20 hidden max-h-[calc(100%-2rem)] w-[min(25rem,calc(100%-2rem))] flex-col items-start gap-3 md:flex"
            >
              <div
                className="pointer-events-auto flex min-h-0 w-[312px] max-w-full flex-col"
                data-testid="problem-links-panel"
              >
                <div
                  className={`flex min-h-0 flex-col overflow-hidden rounded-xl border border-l-2 border-border/30 bg-background/96 shadow-2xl backdrop-blur-md ${statusAccent}`}
                >
                  {/* Header */}
                  <div className="flex shrink-0 items-center gap-2 border-b border-border/25 px-3.5 py-2.5">
                    <div className={`h-1.5 w-1.5 shrink-0 rounded-full ${severityDotClass}`} />
                    <span className="flex-1 text-[9px] font-bold uppercase tracking-[0.22em] text-muted-foreground/70">
                      Problem Links
                    </span>
                    <div className="flex shrink-0 items-center gap-1">
                      {navControls}
                    </div>
                  </div>
                  {/* Body */}
                  {link ? (
                    <div className="min-h-0 overflow-y-auto px-3.5 py-3 thin-scrollbar">
                      <div className="mb-3 flex min-w-0 items-center gap-2">
                        <span className="truncate text-[11px] font-bold text-foreground">{link.id}</span>
                        <span
                          className={`shrink-0 rounded border px-1.5 py-0.5 text-[8px] font-bold uppercase tracking-[0.12em] ${statusBadgeClass}`}
                        >
                          {statusLabel}
                        </span>
                        <button
                          type="button"
                          onClick={() => openSeriesDrawer(link)}
                          className="ml-auto flex h-6 items-center gap-1 rounded border border-border/30 bg-muted/15 px-2 text-[9px] font-semibold text-muted-foreground transition hover:bg-muted/35 hover:text-foreground"
                          title="View link details"
                        >
                          View
                          <svg
                            viewBox="0 0 6 10"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            className="h-2 w-2"
                          >
                            <path d="M1 1l4 4-4 4" />
                          </svg>
                        </button>
                      </div>
                      {detailContent}
                    </div>
                  ) : null}
                </div>
              </div>
              <div className="pointer-events-auto w-full shrink-0">{activeAlertRail}</div>
              </div>
          </>
        );
      })() : null}
      {hasBrokenLinks ? (
        <div className="pointer-events-none absolute inset-0 z-10 map-alert-border" />
      ) : null}
      {/* top status bar removed for mobile readability */}
      {hoverInfo ? (
        <div
          ref={hoverRef}
          className="pointer-events-none fixed z-20 w-64 max-w-[calc(100vw-24px)] rounded-md border border-border/60 bg-background/90 p-3 text-xs text-foreground shadow-lg backdrop-blur"
          style={{
            left: (hoverPosition?.x ?? hoverInfo.x + 12),
            top: (hoverPosition?.y ?? hoverInfo.y + 12)
          }}
        >
          <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">Link discovery</div>
          {isUnconfirmedTopologyLink(hoverInfo.link) ? (
            <div className="mt-1 flex items-center justify-between gap-3 text-sm font-semibold">
              <span className="text-muted-foreground">Confidence</span>
              <span className="text-sky-200">Unconfirmed · one-way · 90%</span>
            </div>
          ) : (
            <div className="mt-1 flex items-center justify-between text-sm font-semibold">
              <span className="text-muted-foreground">Status</span>
              <span
                className={
                  hoverInfo.link.status === LINK_STATUS_MISSING
                    ? 'text-[color:var(--color-info)]'
                    : hoverInfo.link.status === LINK_STATUS_WARNING
                      ? 'text-[color:var(--color-warning)]'
                      : hoverInfo.link.status === 'broken'
                        ? 'text-destructive'
                        : 'text-[color:var(--color-success)]'
                }
              >
                {hoverInfo.link.status}
              </span>
            </div>
          )}
          {!isUnconfirmedTopologyLink(hoverInfo.link) &&
          (hoverInfo.link.status === LINK_STATUS_WARNING || hoverInfo.link.status === 'broken') &&
          hoverInfo.link.status_reason?.summary ? (
            <div className="mt-2 rounded-sm border border-[color:var(--color-warning)]/30 bg-[color:var(--color-warning)]/10 px-2 py-1 text-[11px] text-foreground">
              {hoverInfo.link.status_reason.summary}
            </div>
          ) : null}
          <div className="mt-2 space-y-2 text-muted-foreground">
            {(() => {
              const source = formatHostInterfaceCompact(
                hoverInfo.link.telemetry_host_a,
                hoverInfo.link.telemetry_ifindex_a,
                hoverInfo.link.telemetry_ifname_a
              );
              const target = formatHostInterfaceCompact(
                hoverInfo.link.telemetry_host_b,
                hoverInfo.link.telemetry_ifindex_b,
                hoverInfo.link.telemetry_ifname_b
              );
              return (
                <>
                  <div className="grid grid-cols-[70px_minmax(0,1fr)] items-start gap-2">
                    <span className="text-[11px] uppercase tracking-[0.15em]">A end</span>
                    <span className="min-w-0 text-foreground">
                      <span className="block whitespace-normal break-words">{source.host}</span>
                      <span className="block whitespace-normal break-words text-[10px] text-muted-foreground">
                        {source.iface}
                      </span>
                    </span>
                  </div>
                  <div className="grid grid-cols-[70px_minmax(0,1fr)] items-start gap-2">
                    <span className="text-[11px] uppercase tracking-[0.15em]">Z end</span>
                    <span className="min-w-0 text-foreground">
                      <span className="block whitespace-normal break-words">{target.host}</span>
                      <span className="block whitespace-normal break-words text-[10px] text-muted-foreground">
                        {target.iface}
                      </span>
                    </span>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      ) : null}
      {hoverHubDevice ? (
        <div
          className="pointer-events-none fixed z-20 w-64 max-w-[calc(100vw-24px)] rounded-md border border-border/60 bg-background/90 p-3 text-xs text-foreground shadow-lg backdrop-blur"
          style={{ left: hoverHubDevice.x + 12, top: hoverHubDevice.y + 12 }}
          data-testid="hub-device-health-tooltip"
        >
          <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
            Hub device health
          </div>
          <div className="mt-1 text-sm font-semibold">
            {hoverHubDevice.datum.hub.name ||
              hoverHubDevice.datum.hub.external_id ||
              hoverHubDevice.datum.hub.id}
          </div>
          <div className="mt-2 grid grid-cols-2 gap-1 text-[10px] text-muted-foreground">
            <span>Overall</span>
            <span>{hoverHubDevice.datum.summary.overall}</span>
            <span>Monitored</span>
            <span>{hoverHubDevice.datum.summary.monitored_count}</span>
            <span>Healthy / degraded</span>
            <span>
              {hoverHubDevice.datum.summary.healthy_count} / {hoverHubDevice.datum.summary.degraded_count}
            </span>
            <span>Critical / unknown</span>
            <span>
              {hoverHubDevice.datum.summary.critical_count} / {hoverHubDevice.datum.summary.unknown_count}
            </span>
            <span>Freshness</span>
            <span>{hoverHubDevice.datum.summary.worst_freshness}</span>
            <span>Active alerts</span>
            <span>{hoverHubDevice.datum.summary.confirmed_alert_count}</span>
          </div>
        </div>
      ) : null}
      </div>
      {!publicMode ? <LinkDetailDrawer
        isOpen={drawerOpen}
        onClose={closeSeriesDrawer}
        viewModel={linkDetailViewModel}
        rawLink={selectedLink}
        capacity={formatCapacityMbps(linkCapacity)}
        utilization={formatUtilization(linkUtil)}
        sheet={drawerSheet}
        onSheetChange={setDrawerSheet}
        rangeSeconds={seriesRangeSeconds}
        onRangeSecondsChange={setSeriesRangeSeconds}
        isRealtime={seriesRealtime}
        onIsRealtimeChange={setSeriesRealtime}
        isGrafanaAvailable={snapshot?.observability.grafana_available ?? false}
        onOpenGrafana={onOpenGrafana}
        onRetry={() => setSeriesRetryToken(token => token + 1)}
      /> : null}
      {!publicMode ? <HubDeviceDrawer
        isOpen={hubDeviceDrawer.isOpen}
        view={hubDeviceDrawer.view}
        hub={hubDeviceDrawer.hub}
        selectedDeviceID={hubDeviceDrawer.selectedDeviceID}
        metrics={hubDeviceDrawer.metrics}
        loading={hubDeviceDrawer.loading}
        error={hubDeviceDrawer.error}
        grafanaAvailable={snapshot?.observability.grafana_available ?? false}
        onClose={hubDeviceDrawer.close}
        onSelectDevice={hubDeviceDrawer.selectDevice}
        onBack={hubDeviceDrawer.backToHub}
        onRetry={hubDeviceDrawer.retry}
        onOpenGrafana={onOpenGrafana}
      /> : null}
    </div>
  );
};

export const NetworkMap = (props: NetworkMapProps) => {
  const controller = useStatusTelemetryMapController({
    enabled: !props.staticMode,
    client: props.telemetryClient,
    snapshotPollIntervalMs: props.snapshotPollIntervalMs
  });
  return (
    <StatusTelemetryMapView
      {...props}
      snapshot={controller.snapshot}
      transport={controller.transport}
      freshness={controller.freshness}
      telemetryEstimated={props.staticMode ? false : controller.telemetryEstimated}
      loadError={controller.error}
      focusedAlert={controller.focusedAlert}
      onFocusAlert={controller.focusAlert}
      onOpenGrafana={controller.openGrafana}
    />
  );
};
